import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  translateLocationTextForLanguage,
  transliterateForLanguage,
} from "../utils/transliteration";

const STORAGE_KEY = "nova-realty-language";
const SUPPORTED_LANGUAGES = new Set(["en", "ar", "so"]);
const LANGUAGE_DIRECTIONS = {
  en: "ltr",
  ar: "rtl",
  so: "ltr",
};
const LANGUAGE_LOCALES = {
  en: "en-US",
  ar: "ar",
  so: "so-SO",
};

function replaceBrandName(value) {
  return String(value || "").replace(/Junub Homes/gi, "Nova Realty");
}

const STATIC_TRANSLATIONS = {
  ar: {
    "Verified Listing": "إعلان موثق",
    Rooms: "الغرف",
    Toilets: "الحمامات",
    "Ready to explore": "جاهز للاستعراض",
    "View Details": "عرض التفاصيل",
    Back: "رجوع",
    "Add Property": "إضافة عقار",
    "Listing Overview": "ملخص الإعلان",
    "Property title": "عنوان العقار",
    Price: "السعر",
    Location: "الموقع",
    "Landmark or neighborhood details": "تفاصيل المعلم أو الحي",
    "Describe the property, its condition, and what makes it attractive.":
      "صف العقار وحالته وما يجعله مناسباً.",
    "Select Property Type": "اختر نوع العقار",
    "Select size in m³": "اختر الحجم بالمتر المكعب",
    "Select Space Size in m²": "اختر المساحة بالمتر المربع",
    "Select Listing Type": "اختر نوع الإعلان",
    "Number of Rooms": "عدد الغرف",
    "Number of Toilets": "عدد الحمامات",
    Utilities: "الخدمات",
    "House Rules (Max 6)": "قواعد السكن (6 كحد أقصى)",
    Rule: "قاعدة",
    Remove: "إزالة",
    "Add House Rule": "إضافة قاعدة سكن",
    "Property Owner Details": "تفاصيل مالك العقار",
    "Owner name": "اسم المالك",
    "Owner phone number": "رقم هاتف المالك",
    "Owner email": "بريد المالك الإلكتروني",
    "Property Images": "صور العقار",
    "No images selected yet.": "لم يتم اختيار صور بعد.",
    "Create Property": "إنشاء العقار",
    Creating: "جارٍ الإنشاء...",
    "Welcome Back": "مرحباً بعودتك",
    Email: "البريد الإلكتروني",
    Password: "كلمة المرور",
    Login: "تسجيل الدخول",
    "Signing In...": "جارٍ تسجيل الدخول...",
    "Hide password": "إخفاء كلمة المرور",
    "Show password": "إظهار كلمة المرور",
    "Hide confirm password": "إخفاء تأكيد كلمة المرور",
    "Show confirm password": "إظهار تأكيد كلمة المرور",
    "Resending...": "جارٍ إعادة الإرسال...",
    "Resend Verification Email": "إعادة إرسال بريد التحقق",
    "Create account": "إنشاء حساب",
    "Forgot password?": "نسيت كلمة المرور؟",
    "Create Account": "إنشاء حساب",
    "Full Name": "الاسم الكامل",
    "Phone Number": "رقم الهاتف",
    "Confirm Password": "تأكيد كلمة المرور",
    "Sign Up": "تسجيل",
    "Creating...": "جارٍ الإنشاء...",
    "Already have an account?": "لديك حساب بالفعل؟",
    "Reset Password": "إعادة تعيين كلمة المرور",
    "Send Reset Link": "إرسال رابط إعادة التعيين",
    Sending: "جارٍ الإرسال...",
    "Back to Login": "العودة إلى تسجيل الدخول",
    "Your Properties": "عقاراتك",
    "Check Listings": "تصفح الإعلانات",
    "Loading your properties...": "جارٍ تحميل عقاراتك...",
    "No properties yet": "لا توجد عقارات بعد",
    "Edit Property": "تعديل العقار",
    Title: "العنوان",
    "Location Details": "تفاصيل الموقع",
    "Owner Name": "اسم المالك",
    "Owner Phone": "هاتف المالك",
    "Owner Email": "بريد المالك",
    "Property Type": "نوع العقار",
    "Listing Type": "نوع الإعلان",
    Description: "الوصف",
    "Add Images": "إضافة صور",
    "Save Changes": "حفظ التغييرات",
    Saving: "جارٍ الحفظ...",
    Cancel: "إلغاء",
    Edit: "تعديل",
    Delete: "حذف",
    "Account Suspended": "الحساب موقوف",
    "Account Rejected": "تم رفض الحساب",
    "Account Terminated": "تم إنهاء الحساب",
    "Account Pending Approval": "الحساب بانتظار الموافقة",
    "Your account has been suspended by the administrator.":
      "تم إيقاف حسابك بواسطة المسؤول.",
    "Your account request was rejected by the administrator.":
      "تم رفض طلب حسابك بواسطة المسؤول.",
    "This account has been terminated and can no longer manage listings.":
      "تم إنهاء هذا الحساب ولا يمكنه إدارة الإعلانات.",
    "Your account has been created successfully and is waiting for administrator approval.":
      "تم إنشاء حسابك بنجاح وهو بانتظار موافقة المسؤول.",
    Explore: "استكشف",
    Home: "الرئيسية",
    "For Rent": "للإيجار",
    "For Sale": "للبيع",
    "For Lease": "للإيجار بعقد",
    "Short Stay": "إقامة قصيرة",
    Company: "الشركة",
    About: "من نحن",
    Services: "الخدمات",
    Contact: "اتصل بنا",
    "Privacy Policy": "سياسة الخصوصية",
    "Terms and Conditions": "الشروط والأحكام",
    "Homes, apartments, shops, stores, and short stays across Nairobi.":
      "منازل وشقق ومحلات ومخازن وإقامات قصيرة في نيروبي.",
    "All rights reserved.": "جميع الحقوق محفوظة.",
    "About Junub Homes": "عن Junub Homes",
    "A focused property platform for Nairobi.": "منصة عقارية متخصصة لنيروبي.",
    "Junub Homes helps people find places to live, buy, rent, stay, or operate a business by bringing approved property listings into one clear and searchable experience.":
      "تساعد Junub Homes الناس على العثور على أماكن للسكن أو الشراء أو الإيجار أو الإقامة أو تشغيل الأعمال من خلال إعلانات عقارية معتمدة في تجربة واضحة وقابلة للبحث.",
    "What We Do": "ماذا نفعل",
    "We make property discovery simpler and more organized.":
      "نجعل اكتشاف العقارات أبسط وأكثر تنظيماً.",
    "Our goal is to reduce the friction between property seekers and trusted listings. The site supports rental homes, sale properties, commercial spaces, stores, and short-stay options, with tools for agents and administrators to keep information current.":
      "هدفنا هو تقليل العوائق بين الباحثين عن العقارات والإعلانات الموثوقة. يدعم الموقع منازل الإيجار وعقارات البيع والمساحات التجارية والمخازن وخيارات الإقامة القصيرة مع أدوات للوكلاء والمسؤولين لتحديث المعلومات.",
    "Verified Workflow": "آلية موثقة",
    "Listings are reviewed before they appear publicly, helping renters and buyers browse with more confidence.":
      "تتم مراجعة الإعلانات قبل ظهورها للعامة، مما يساعد المستأجرين والمشترين على التصفح بثقة أكبر.",
    "Local Property Focus": "تركيز عقاري محلي",
    "The platform is built around real property needs in Nairobi, from homes and apartments to shops, stores, and short stays.":
      "تم بناء المنصة حول احتياجات العقارات الحقيقية في نيروبي، من المنازل والشقق إلى المحلات والمخازن والإقامات القصيرة.",
    "Clear Connections": "تواصل واضح",
    "Property seekers can quickly reach the right contact while agents and owners keep their listings organized.":
      "يمكن للباحثين عن العقارات الوصول بسرعة إلى جهة الاتصال المناسبة بينما يحافظ الوكلاء والمالكون على تنظيم إعلاناتهم.",
    "Our Promise": "وعدنا",
    "We aim to keep listings useful, contacts clear, and the browsing experience fast enough for everyday property decisions.":
      "نسعى إلى إبقاء الإعلانات مفيدة وجهات الاتصال واضحة وتجربة التصفح سريعة بما يكفي لقرارات العقار اليومية.",
    "Practical tools for listing and finding property.":
      "أدوات عملية لإدراج العقارات والعثور عليها.",
    "Junub Homes supports the full listing journey, from publishing property details to helping visitors discover and contact the right people.":
      "تدعم Junub Homes رحلة الإعلان كاملة، من نشر تفاصيل العقار إلى مساعدة الزوار على اكتشاف الأشخاص المناسبين والتواصل معهم.",
    "Property Listings": "إعلانات العقارات",
    "Publish homes, apartments, commercial spaces, stores, and short-stay units with structured details and images.":
      "انشر المنازل والشقق والمساحات التجارية والمخازن ووحدات الإقامة القصيرة مع تفاصيل وصور منظمة.",
    "Property Search": "البحث عن عقار",
    "Browse approved listings by type, price, rooms, listing purpose, and location to narrow decisions faster.":
      "تصفح الإعلانات المعتمدة حسب النوع والسعر والغرف والغرض والموقع لتضييق الخيارات بسرعة.",
    "Listing Review": "مراجعة الإعلانات",
    "Admin review tools help keep public listings organized, approved, and easier for visitors to trust.":
      "تساعد أدوات مراجعة المسؤول في إبقاء الإعلانات العامة منظمة ومعتمدة وأسهل للثقة بها.",
    "Property Marketing": "تسويق العقارات",
    "Featured advert placements help highlight important offers, agencies, and property campaigns.":
      "تساعد مساحات الإعلان المميزة في إبراز العروض المهمة والوكالات والحملات العقارية.",
    "Ready to browse live listings?": "هل أنت جاهز لتصفح الإعلانات المتاحة؟",
    "Start with rentals, sale properties, or short-stay options and use filters to narrow the list.":
      "ابدأ بالإيجارات أو عقارات البيع أو خيارات الإقامة القصيرة واستخدم الفلاتر لتضييق القائمة.",
    "Contact Us": "اتصل بنا",
    "Talk to Junub Homes.": "تحدث إلى Junub Homes.",
    "Send property questions, listing requests, support issues, or partnership inquiries. The team will review your message from the admin dashboard.":
      "أرسل أسئلة العقارات أو طلبات الإعلان أو مشكلات الدعم أو استفسارات الشراكة. سيراجع الفريق رسالتك من لوحة الإدارة.",
    Name: "الاسم",
    Subject: "الموضوع",
    Message: "الرسالة",
    "Your full name": "اسمك الكامل",
    "Best number to reach you": "أفضل رقم للتواصل معك",
    "How can we help?": "كيف يمكننا مساعدتك؟",
    "Share the property type, budget, location, or support details.":
      "شارك نوع العقار أو الميزانية أو الموقع أو تفاصيل الدعم.",
    "Send Message": "إرسال الرسالة",
    Phone: "الهاتف",
    "Add your phone number here": "أضف رقم هاتفك هنا",
    "Service Area": "منطقة الخدمة",
    "Nairobi, Kenya": "نيروبي، كينيا",
    "Effective date:": "تاريخ السريان:",
    "Last updated:": "آخر تحديث:",
    "Information We Collect": "المعلومات التي نجمعها",
    "How We Use Information": "كيف نستخدم المعلومات",
    "Property Listings and Public Content": "إعلانات العقارات والمحتوى العام",
    "Sharing Information": "مشاركة المعلومات",
    "Data Storage and Security": "تخزين البيانات والأمان",
    "Your Choices": "خياراتك",
    "Cookies and Similar Technologies": "ملفات تعريف الارتباط والتقنيات المشابهة",
    "Changes to This Policy": "التغييرات على هذه السياسة",
    "Contact Us Privacy": "اتصل بنا",
    "Please fill in your name, phone number, subject, and message.":
      "يرجى تعبئة الاسم ورقم الهاتف والموضوع والرسالة.",
    "Please enter a valid phone number.": "يرجى إدخال رقم هاتف صحيح.",
    "Thanks. Your message has been sent to the Junub Homes team.":
      "شكراً لك. تم إرسال رسالتك إلى فريق Junub Homes.",
    "We could not send your message right now. Please try again.":
      "تعذر إرسال رسالتك الآن. يرجى المحاولة مرة أخرى.",
    "This starter policy explains how Junub Homes may collect, use, and protect information from visitors, property seekers, agents, and property owners.":
      "توضح هذه السياسة كيف يمكن لـ Junub Homes جمع واستخدام وحماية معلومات الزوار والباحثين عن العقارات والوكلاء ومالكي العقارات.",
    "We may collect information you provide directly, including your name, phone number, email address, account details, property details, listing images, messages, and contact requests.":
      "قد نجمع المعلومات التي تقدمها مباشرة، بما في ذلك اسمك ورقم هاتفك وبريدك الإلكتروني وتفاصيل الحساب وتفاصيل العقار وصور الإعلان والرسائل وطلبات التواصل.",
    "We may also collect basic technical information such as device type, browser type, pages visited, and general usage activity to keep the service reliable and improve the user experience.":
      "قد نجمع أيضاً معلومات تقنية أساسية مثل نوع الجهاز والمتصفح والصفحات التي تمت زيارتها والنشاط العام للحفاظ على موثوقية الخدمة وتحسين تجربة المستخدم.",
    "We use information to create and manage accounts, publish and review listings, connect users with agents or property owners, respond to inquiries, prevent misuse, improve our services, and communicate important updates.":
      "نستخدم المعلومات لإنشاء الحسابات وإدارتها ونشر الإعلانات ومراجعتها وربط المستخدمين بالوكلاء أو مالكي العقارات والرد على الاستفسارات ومنع إساءة الاستخدام وتحسين خدماتنا وإرسال التحديثات المهمة.",
    "Information included in approved property listings, such as property images, location details, prices, descriptions, and agent contact details, may be visible to visitors using the website.":
      "قد تكون المعلومات الواردة في الإعلانات العقارية المعتمدة، مثل صور العقار وتفاصيل الموقع والأسعار والأوصاف وتفاصيل اتصال الوكيل، مرئية لزوار الموقع.",
    "We do not sell personal information. We may share information when needed to provide the service, comply with legal obligations, protect users, investigate misuse, or work with trusted service providers that help operate the website.":
      "نحن لا نبيع المعلومات الشخصية. قد نشارك المعلومات عند الحاجة لتقديم الخدمة أو الامتثال للالتزامات القانونية أو حماية المستخدمين أو التحقيق في إساءة الاستخدام أو العمل مع مزودي خدمات موثوقين يساعدون في تشغيل الموقع.",
    "We use reasonable administrative, technical, and organizational measures to protect information. No online service can guarantee complete security, so users should avoid submitting unnecessary sensitive information.":
      "نستخدم إجراءات إدارية وتقنية وتنظيمية معقولة لحماية المعلومات. لا يمكن لأي خدمة عبر الإنترنت ضمان الأمان الكامل، لذلك يجب على المستخدمين تجنب إرسال معلومات حساسة غير ضرورية.",
    "You may request updates, corrections, or deletion of certain account or listing information by contacting us. Some information may need to be retained for legitimate business, safety, or legal reasons.":
      "يمكنك طلب تحديث أو تصحيح أو حذف بعض معلومات الحساب أو الإعلان عن طريق التواصل معنا. قد نحتاج إلى الاحتفاظ ببعض المعلومات لأسباب تجارية أو أمنية أو قانونية مشروعة.",
    "The website may use cookies or similar technologies to support authentication, remember preferences, measure performance, and improve reliability.":
      "قد يستخدم الموقع ملفات تعريف الارتباط أو تقنيات مشابهة لدعم المصادقة وتذكر التفضيلات وقياس الأداء وتحسين الموثوقية.",
    "We may update this policy from time to time. When changes are made, we will update the date above and publish the revised policy on this page.":
      "قد نقوم بتحديث هذه السياسة من وقت لآخر. عند إجراء تغييرات، سنحدث التاريخ أعلاه وننشر السياسة المعدلة على هذه الصفحة.",
    "For privacy questions or requests, contact us at":
      "لأسئلة أو طلبات الخصوصية، تواصل معنا عبر",
    TV: "تلفزيون",
    DStv: "دي إس تي في",
    "Wi-Fi": "واي فاي",
    "Air Conditioning": "تكييف",
    Security: "أمن",
    "Backup Electricity": "كهرباء احتياطية",
    Parking: "موقف سيارات",
    "Swimming Pool": "مسبح",
    Gym: "صالة ألعاب رياضية",
    Pending: "قيد الانتظار",
    Approved: "معتمد",
    Rejected: "مرفوض",
    uploaded: "تم الرفع",
    "Sending...": "جارٍ الإرسال...",
    "Submitting...": "جارٍ الإرسال...",
    "Submit Property": "إرسال العقار",
  },
};

Object.assign(STATIC_TRANSLATIONS.ar, {
  "Add Car": "إضافة سيارة",
  Cars: "السيارات",
  "Your Cars": "سياراتك",
  "Your Profile": "ملفك الشخصي",
  Profile: "الملف الشخصي",
  Properties: "العقارات",
  Users: "المستخدمون",
  Adverts: "الإعلانات",
  Subscriptions: "الاشتراكات",
  Messages: "الرسائل",
  pending: "قيد الانتظار",
  new: "جديد",
  approved: "معتمد",
  rejected: "مرفوض",
  "Add a car for rent or sale.": "أضف سيارة للإيجار أو البيع.",
  "Basic Information": "المعلومات الأساسية",
  "Car brand and model": "ماركة وموديل السيارة",
  "Year of manufacture": "سنة الصنع",
  "Listing type": "نوع الإعلان",
  "Body type": "نوع الهيكل",
  Transmission: "ناقل الحركة",
  "Fuel type": "نوع الوقود",
  "Engine size": "سعة المحرك",
  Mileage: "المسافة المقطوعة",
  "Drive type": "نظام الدفع",
  Color: "اللون",
  "Condition Details": "تفاصيل الحالة",
  "Mechanical condition": "الحالة الميكانيكية",
  "Interior condition": "حالة الداخل",
  "Exterior condition": "حالة الخارج",
  "Accident history": "سجل الحوادث",
  "Registration history": "سجل التسجيل",
  "Service history": "سجل الصيانة",
  "Features / Extras": "المزايا والإضافات",
  Features: "المزايا",
  "Price & Contact": "السعر والتواصل",
  Currency: "العملة",
  "Phone number": "رقم الهاتف",
  "WhatsApp number": "رقم واتساب",
  Negotiable: "قابل للتفاوض",
  Yes: "نعم",
  No: "لا",
  "Car Images": "صور السيارة",
  "{count} image(s) selected.": "تم اختيار {count} صورة.",
  "{count} image(s) selected": "تم اختيار {count} صورة",
  "Upload up to {count} pictures.": "ارفع حتى {count} صور.",
  "Submit Car Listing": "إرسال إعلان السيارة",
  "Preparing listing...": "جارٍ تجهيز الإعلان...",
  "Uploading images...": "جارٍ رفع الصور...",
  "Saving listing...": "جارٍ حفظ الإعلان...",
  "Submitting...": "جارٍ الإرسال...",
  "Saving...": "جارٍ الحفظ...",
  "Please keep this page open while we finish.": "يرجى إبقاء هذه الصفحة مفتوحة حتى ننتهي.",
  "Car listing added successfully.": "تمت إضافة إعلان السيارة بنجاح.",
  "We could not save the car listing.": "تعذر حفظ إعلان السيارة.",
  "Image upload failed. Please try again.": "فشل رفع الصورة. يرجى المحاولة مرة أخرى.",
  "Price is required.": "السعر مطلوب.",
  "Currency is required.": "العملة مطلوبة.",
  "Price must be greater than 0.": "يجب أن يكون السعر أكبر من صفر.",
  "Upload at least one image.": "ارفع صورة واحدة على الأقل.",
  Sedan: "سيدان",
  SUV: "دفع رباعي",
  Pickup: "بيك أب",
  Hatchback: "هاتشباك",
  Van: "فان",
  Truck: "شاحنة",
  Bus: "باص",
  Other: "أخرى",
  Automatic: "أوتوماتيك",
  Manual: "عادي",
  Petrol: "بنزين",
  Diesel: "ديزل",
  Hybrid: "هجين",
  Electric: "كهربائي",
  "Junub Homes Auto": "سيارات Junub Homes",
  "Find the right car for work, travel, rent, or ownership.": "اعثر على السيارة المناسبة للعمل أو السفر أو الإيجار أو التملك.",
  "Search brand, model, location": "ابحث بالماركة أو الموديل أو الموقع",
  "Rent or sale": "إيجار أو بيع",
  Fuel: "الوقود",
  "Max price": "أعلى سعر",
  "Featured Cars": "السيارات المميزة",
  "{count} available": "{count} متاح",
  "Loading cars...": "جارٍ تحميل السيارات...",
  "No approved cars match your filters.": "لا توجد سيارات معتمدة تطابق الفلاتر.",
  "Why choose us": "لماذا تختارنا",
  "Car listing not found.": "لم يتم العثور على إعلان السيارة.",
  "Loading car...": "جارٍ تحميل السيارة...",
  "Car unavailable.": "السيارة غير متاحة.",
  "Rental Price": "سعر الإيجار",
  "Car Price": "سعر السيارة",
  Body: "الهيكل",
  Engine: "المحرك",
  Drive: "الدفع",
  "Not provided": "غير متوفر",
  "All Pictures": "كل الصور",
  "Contact Junub Homes": "تواصل مع Junub Homes",
  "Request car contact": "طلب تواصل بخصوص السيارة",
  Year: "السنة",
  "Admin Dashboard": "لوحة الإدارة",
  "New Messages": "رسائل جديدة",
  "Contact Requests": "طلبات التواصل",
  "All Status": "كل الحالات",
  Pending: "قيد الانتظار",
  Approved: "معتمد",
  Rejected: "مرفوض",
  Agent: "الوكيل",
  Approve: "اعتماد",
  Reject: "رفض",
  "Edit Car": "تعديل السيارة",
  Close: "إغلاق",
  WhatsApp: "واتساب",
  "Listing Contact Requests": "طلبات التواصل مع الإعلانات",
  All: "الكل",
  Unread: "غير مقروء",
  Read: "مقروء",
  "Listing ID": "معرف الإعلان",
  "Contact Route": "مسار التواصل",
  "Agent Details": "تفاصيل الوكيل",
  "Owner Details": "تفاصيل المالك",
  Owner: "المالك",
  "Agent Profile": "ملف الوكيل",
  "Property & Car Listings": "إعلانات العقارات والسيارات",
  "Car Rentals & Sales": "إيجار وبيع السيارات",
  "Property & Car Search": "البحث عن العقارات والسيارات",
  "Listing Marketing": "تسويق الإعلانات",
  "Practical tools for listing and finding properties and cars.": "أدوات عملية لإدراج العقارات والسيارات والعثور عليها.",
  "Local Listing Focus": "تركيز محلي على الإعلانات",
  "Cars in Nairobi": "السيارات في نيروبي",
});

Object.assign(STATIC_TRANSLATIONS.ar, {
  "Our values": "قيمنا",
  "Junub Homes helps people find places to buy, rent, stay, or operate a business, and cars to rent or buy, by bringing approved listings into one clear and searchable experience.":
    "تساعد Junub Homes الناس على العثور على أماكن للشراء أو الإيجار أو الإقامة أو تشغيل عمل، وعلى سيارات للإيجار أو الشراء، من خلال جمع الإعلانات المعتمدة في تجربة واضحة وقابلة للبحث.",
  "Our goal is to reduce the friction between people looking for trusted properties, car rentals, and vehicle sales in Nairobi. The site supports rental homes, sale properties, commercial spaces, stores, short-stay options, and cars, with tools for agents and administrators to keep information current.":
    "هدفنا هو تقليل العوائق أمام الأشخاص الباحثين عن عقارات موثوقة وإيجار السيارات وبيعها في نيروبي. يدعم الموقع منازل الإيجار وعقارات البيع والمساحات التجارية والمخازن وخيارات الإقامة القصيرة والسيارات، مع أدوات للوكلاء والمسؤولين لإبقاء المعلومات محدثة.",
  "The platform is built around real property and car needs in Nairobi, from homes and apartments to shops, stores, short stays, rentals, and vehicle sales.":
    "تم بناء المنصة حول احتياجات العقارات والسيارات الحقيقية في نيروبي، من المنازل والشقق إلى المحلات والمخازن والإقامات القصيرة والإيجارات وبيع السيارات.",
  "Car rentals and sales sit beside property listings so people can find a home, space, or ride from one trusted place.":
    "تقع إيجارات السيارات وبيعها إلى جانب إعلانات العقارات ليتمكن الناس من العثور على منزل أو مساحة أو وسيلة تنقل من مكان موثوق واحد.",
  "We aim to keep listings useful, contacts clear, and the browsing experience fast enough for everyday property and car decisions.":
    "نسعى إلى إبقاء الإعلانات مفيدة وجهات الاتصال واضحة وتجربة التصفح سريعة بما يكفي لقرارات العقار والسيارات اليومية.",
  "Junub Homes supports the full listing journey, from publishing property and car details to helping visitors discover and contact the right people.":
    "تدعم Junub Homes رحلة الإعلان كاملة، من نشر تفاصيل العقارات والسيارات إلى مساعدة الزوار على اكتشاف الأشخاص المناسبين والتواصل معهم.",
  "Publish homes, apartments, commercial spaces, stores, short-stay units, and cars with structured details and images.":
    "انشر المنازل والشقق والمساحات التجارية والمخازن ووحدات الإقامة القصيرة والسيارات مع تفاصيل وصور منظمة.",
  "List cars for rent or sale in Nairobi and help buyers or renters reach Junub Homes quickly.":
    "أدرج سيارات للإيجار أو البيع في نيروبي وساعد المشترين أو المستأجرين على الوصول إلى Junub Homes بسرعة.",
  "Browse approved properties and cars by type, price, purpose, and location to narrow decisions faster.":
    "تصفح العقارات والسيارات المعتمدة حسب النوع والسعر والغرض والموقع لتضييق الخيارات بسرعة.",
  "Featured advert placements help highlight important offers, agencies, property campaigns, and vehicle deals.":
    "تساعد مساحات الإعلان المميزة في إبراز العروض المهمة والوكالات والحملات العقارية وعروض المركبات.",
  "properties and car rentals, sales, and listing support in Nairobi. Contact us to list your property, or explore our website when you are ready to find your next home, space, or ride ":
    "العقارات وإيجار السيارات وبيعها ودعم الإعلانات في نيروبي. تواصل معنا لإدراج عقارك، أو تصفح موقعنا عندما تكون جاهزاً للعثور على منزلك أو مساحتك أو وسيلة تنقلك التالية.",
});

Object.assign(STATIC_TRANSLATIONS.ar, {
  "Hotel & Corporate Travel Requests": "طلبات الفنادق والسفر المؤسسي",
  "Coordinate hotels, room blocks, airport pickup, and concierge support for NGOs, embassies, medical teams, and company travelers.":
    "نسق الفنادق وحجوزات الغرف والاستقبال من المطار ودعم الكونسيرج للمنظمات والسفارات والفرق الطبية ومسافري الشركات.",
  "Start with property rentals, sale properties, short-stay options, or cars and use filters to narrow the list.":
    "ابدأ بعقارات الإيجار أو البيع أو خيارات الإقامة القصيرة أو السيارات، واستخدم الفلاتر لتضييق القائمة.",
  "Send property questions, listing requests, support issues, or partnership inquiries. And our team will review your message.":
    "أرسل أسئلة العقارات أو طلبات الإعلانات أو مشكلات الدعم أو استفسارات الشراكة، وسيقوم فريقنا بمراجعة رسالتك.",
  Administrators: "المشرفون",
  "Reach the Junub Homes administrators directly using the details below.":
    "تواصل مباشرة مع مشرفي Junub Homes باستخدام التفاصيل أدناه.",
  "Loading contact details...": "جارٍ تحميل تفاصيل التواصل...",
  "No active administrators are available right now.":
    "لا يوجد مشرفون نشطون متاحون الآن.",
  "Contact admin for price": "اتصل بالإدارة لمعرفة السعر",
  "Tap below to view": "اضغط أدناه للعرض",
  "Opening contact...": "جارٍ فتح جهة الاتصال...",
  "Contact open": "تم فتح جهة الاتصال",
  Introduction: "مقدمة",
  "Acceptance of Terms": "قبول الشروط",
  "User Eligibility": "أهلية المستخدم",
  "Account Registration": "تسجيل الحساب",
  "No Legal or Financial Advice": "لا توجد استشارة قانونية أو مالية",
  "Business Affiliates": "الجهات التابعة للأعمال",
  "Referral Disclosure": "الإفصاح عن الإحالات",
  "Commissions and Service Fees": "العمولات ورسوم الخدمة",
  "Third-Party Services": "خدمات الطرف الثالث",
  "User Responsibilities": "مسؤوليات المستخدم",
  "Intellectual Property": "الملكية الفكرية",
  "Privacy and Data Protection": "الخصوصية وحماية البيانات",
  "Disclaimer of Warranties": "إخلاء المسؤولية عن الضمانات",
  "Limitation of Liability": "حدود المسؤولية",
  Indemnification: "التعويض",
  "Force Majeure": "القوة القاهرة",
  Termination: "إنهاء الخدمة",
  "Governing Law": "القانون الحاكم",
  "Dispute Resolution": "تسوية النزاعات",
  Severability: "قابلية الفصل",
  "Entire Agreement": "الاتفاق الكامل",
  "Contact Information": "معلومات الاتصال",
  "These Terms and Conditions govern how visitors, clients, agents, landlords, sellers, buyers, and other users access and use the Junub Homes website and related services.":
    "تنظم هذه الشروط والأحكام كيفية وصول الزوار والعملاء والوكلاء والملاك والبائعين والمشترين وغيرهم من المستخدمين إلى موقع Junub Homes والخدمات المرتبطة به واستخدامهم لها.",
  "Welcome to Junub Homes, a registered real estate brokerage business operating in Nairobi, Republic of Kenya. These Terms govern access to and use of all Junub Homes services, platforms, and website content.":
    "مرحبًا بكم في Junub Homes، وهي شركة وساطة عقارية مسجلة تعمل في نيروبي بجمهورية كينيا. تنظم هذه الشروط الوصول إلى جميع خدمات Junub Homes ومنصاتها ومحتوى موقعها واستخدامها.",
  "Junub Homes provides real estate brokerage, property marketing, rentals, leasing, investment advisory, property management referrals, and related consultancy services.":
    "تقدم Junub Homes خدمات الوساطة العقارية وتسويق العقارات والإيجارات والتأجير والاستشارات الاستثمارية وإحالات إدارة العقارات والخدمات الاستشارية ذات الصلة.",
  "By using our Services, you confirm that you have legal capacity to enter binding agreements and agree to comply with all applicable laws.":
    "باستخدامك خدماتنا، فإنك تؤكد أن لديك الأهلية القانونية لإبرام اتفاقيات ملزمة وتوافق على الالتزام بجميع القوانين المعمول بها.",
  "Users must be at least 18 years old, not previously suspended from our Services, and legally permitted to use our Services.":
    "يجب أن يكون عمر المستخدمين 18 عامًا على الأقل، وألا يكونوا قد أوقفوا سابقًا من خدماتنا، وأن يكونوا مخولين قانونيًا لاستخدامها.",
  "Users may be required to provide accurate registration information and are responsible for maintaining the confidentiality of account credentials.":
    "قد يُطلب من المستخدمين تقديم معلومات تسجيل دقيقة، وهم مسؤولون عن الحفاظ على سرية بيانات اعتماد الحساب.",
  "Property information may originate from owners, developers, landlords, agents, or third parties. Junub Homes does not guarantee ownership, accuracy, market value, legal status, or suitability of any property.":
    "قد تأتي معلومات العقار من المالكين أو المطورين أو المؤجرين أو الوكلاء أو أطراف ثالثة. لا تضمن Junub Homes الملكية أو الدقة أو القيمة السوقية أو الوضع القانوني أو ملاءمة أي عقار.",
  "Information provided through the website is for informational purposes only and does not constitute legal, tax, investment, engineering, or financial advice.":
    "المعلومات المقدمة عبر الموقع هي لأغراض معلوماتية فقط ولا تشكل استشارة قانونية أو ضريبية أو استثمارية أو هندسية أو مالية.",
  "Junub Homes maintains professional affiliations with Kafuki Jada Blog and N&K Co. Advocates and Legal Consultants.":
    "تحتفظ Junub Homes بعلاقات مهنية مع Kafuki Jada Blog و N&K Co. Advocates and Legal Consultants.",
  "Users acknowledge that referrals between affiliated businesses may generate commissions, referral fees, or financial benefits.":
    "يقر المستخدمون بأن الإحالات بين الجهات التابعة قد تولد عمولات أو رسوم إحالة أو منافع مالية.",
  "For property rentals, a commission of one month rent for 12 months advanced rent shall be payable for successfully facilitated transactions, and half a month rent for 6 months advanced payment, unless otherwise agreed in writing.":
    "في إيجارات العقارات، تُستحق عمولة تعادل إيجار شهر واحد عند سداد إيجار 12 شهرًا مقدمًا للمعاملات التي تم تسهيلها بنجاح، ونصف إيجار شهر عند سداد 6 أشهر مقدمًا، ما لم يُتفق على خلاف ذلك كتابةً.",
  "For short stays, including hotels and hotel apartments, and for car rentals, a commission of five percent (5%) of the amount paid shall be charged.":
    "في الإقامات القصيرة، بما في ذلك الفنادق والشقق الفندقية، وكذلك في إيجارات السيارات، تُفرض عمولة قدرها خمسة بالمئة (5%) من المبلغ المدفوع.",
  "For property and car sales, a commission of ten percent (10%) of the total transaction value shall be payable for successfully facilitated transactions unless otherwise agreed in writing.":
    "في مبيعات العقارات والسيارات، تُستحق عمولة قدرها عشرة بالمئة (10%) من إجمالي قيمة المعاملة للصفقات التي تم تسهيلها بنجاح، ما لم يُتفق على خلاف ذلك كتابةً.",
  "Links to third-party services are provided for convenience only. Junub Homes does not control or guarantee such services.":
    "تُقدم روابط خدمات الطرف الثالث لراحة المستخدم فقط. ولا تتحكم Junub Homes في هذه الخدمات ولا تضمنها.",
  "Users shall not provide false information, engage in fraud, misrepresent property ownership, or interfere with website operations.":
    "يجب على المستخدمين عدم تقديم معلومات كاذبة أو ارتكاب احتيال أو تحريف ملكية العقار أو التدخل في تشغيل الموقع.",
  "All logos, trademarks, designs, text, graphics, databases, and marketing materials remain the property of Junub Homes or its licensors.":
    "تظل جميع الشعارات والعلامات التجارية والتصاميم والنصوص والرسومات وقواعد البيانات والمواد التسويقية ملكًا لـ Junub Homes أو للمرخصين لها.",
  "Users consent to the collection, storage, and processing of personal information necessary for service delivery and legal compliance.":
    "يوافق المستخدمون على جمع المعلومات الشخصية اللازمة لتقديم الخدمة والامتثال القانوني وتخزينها ومعالجتها.",
  "Services are provided on an 'AS IS' and 'AS AVAILABLE' basis without warranties of any kind.":
    "تُقدَّم الخدمات على أساس \"كما هي\" و\"حسب التوفر\" دون أي ضمانات من أي نوع.",
  "Junub Homes shall not be liable for direct, indirect, consequential, incidental, or special damages arising from the use of its Services.":
    "لا تتحمل Junub Homes المسؤولية عن الأضرار المباشرة أو غير المباشرة أو التبعية أو العرضية أو الخاصة الناشئة عن استخدام خدماتها.",
  "Users agree to indemnify and hold harmless Junub Homes and its affiliates from claims arising from misuse of Services or breach of these Terms.":
    "يوافق المستخدمون على تعويض Junub Homes والجهات التابعة لها وإبراء ذمتها من المطالبات الناشئة عن إساءة استخدام الخدمات أو خرق هذه الشروط.",
  "Junub Homes shall not be liable for delays or failures caused by events beyond reasonable control, including natural disasters, conflict, or telecommunications failures.":
    "لا تتحمل Junub Homes المسؤولية عن التأخير أو الإخفاق الناتج عن أحداث خارجة عن السيطرة المعقولة، بما في ذلك الكوارث الطبيعية أو النزاعات أو أعطال الاتصالات.",
  "Junub Homes reserves the right to suspend or terminate access to Services where these Terms are violated.":
    "تحتفظ Junub Homes بالحق في تعليق أو إنهاء الوصول إلى الخدمات عند انتهاك هذه الشروط.",
  "These Terms shall be governed by the laws of the Republic of Kenya.":
    "تخضع هذه الشروط لقوانين جمهورية كينيا.",
  "Disputes shall first be resolved amicably. Unresolved disputes shall be submitted to competent courts in Kenya.":
    "تُحل النزاعات أولاً وديًا. وإذا تعذر حلها، تُحال إلى المحاكم المختصة في كينيا.",
  "If any provision is deemed unenforceable, the remaining provisions shall remain valid and enforceable.":
    "إذا اعتُبر أي بند غير قابل للتنفيذ، فستظل البنود الأخرى سارية ونافذة.",
  "These Terms constitute the entire agreement between Junub Homes and users regarding the Services.":
    "تشكل هذه الشروط الاتفاق الكامل بين Junub Homes والمستخدمين فيما يتعلق بالخدمات.",
  "Nairobi, Republic of Kenya": "نيروبي، جمهورية كينيا",
  "Telephone: 0980430211 / +211929929949": "الهاتف: 0980430211 / +211929929949",
  "Email: virmoduho@gmail.com": "البريد الإلكتروني: virmoduho@gmail.com",
  "Website: https://junubhomes.com": "الموقع الإلكتروني: https://junubhomes.com",
});

Object.assign(STATIC_TRANSLATIONS.ar, {
  Bedrooms: "غرف النوم",
  Bathrooms: "الحمامات",
  "Number of Bedrooms": "عدد غرف النوم",
  "Number of Bathrooms": "عدد الحمامات",
  "Room Features": "مميزات الغرف",
  Kitchen: "مطبخ",
  "Dining Room": "غرفة الطعام",
  "Sitting Room": "غرفة الجلوس",
  "Commercial Building": "مبنى تجاري",
  DStv: "دي إس تي في",
  "Swimming Pool": "مسبح",
  Gym: "صالة ألعاب رياضية",
});

STATIC_TRANSLATIONS.so = STATIC_TRANSLATIONS.so || {};

Object.assign(STATIC_TRANSLATIONS.so, {
  Bedrooms: "Qolalka jiifka",
  Bathrooms: "Musqulaha",
  "Number of Bedrooms": "Tirada qolalka jiifka",
  "Number of Bathrooms": "Tirada musqulaha",
  "Room Features": "Astaamaha qolalka",
  Kitchen: "Jiko",
  "Dining Room": "Qolka cuntada",
  "Sitting Room": "Qolka fadhiga",
  "Commercial Building": "Dhisme Ganacsi",
  DStv: "DStv",
  "Swimming Pool": "Barkad dabaal",
  Gym: "Jimicsi",
});

const TRANSLATIONS = {
  en: {
    language: {
      label: "Language",
      english: "English",
      arabic: "Arabic",
    },
    nav: {
      home: "Home",
      shortStay: "Short Stay",
      addProperty: "Add Property",
      privateListings: "Private Listings",
      about: "About Us",
      services: "Services",
      contact: "Contact",
      admin: "Admin",
      logout: "Logout",
      login: "Login",
      loggedOut: "Logged out successfully",
      toggleMenu: "Toggle navigation menu",
    },
    home: {
      heroDefault:
        "Find your next home or business premises from the comfort of your home.",
      heroSubtitle: "",
      heroListing: "{listingType} Properties",
      searchPlaceholder: "Search by title, location, or keyword",
      mobileSearchPlaceholder: "Search location or title...",
      minPrice: "Min Price",
      maxPrice: "Max Price",
      rooms: "Bedrooms",
      propertyType: "Property Type",
      listingType: "Listing Type",
      clearFilters: "Clear Filters",
      resetFilters: "Reset Filters",
      filters: "Filters",
      filterProperties: "Filter Properties",
      browseByCategory: "Browse by Category",
      availableProperties: "Available Properties",
      availableCategory: "Available {category}",
      loadingListings: "Loading listings...",
      approvedListingsFound: "{count} approved listing(s) found",
      loading: "Loading...",
      listingsReady: "{count} listings ready",
      allListingTypes: "All listing types",
      allPropertyTypes: "All property types",
      loadingApproved: "Loading approved properties...",
      loadFailed: "We could not load approved listings right now.",
      noMatches: "No properties match the current search and filters.",
      reset: "Reset",
      shortStayCount: "Short Stay · {count} listing{plural}",
      quickStays: "Quick Stays Ready To Book",
      noShortStays: "No short-stay listings are live right now.",
      previousAvailableProperties: "Show previous property listings",
      nextAvailableProperties: "Show next property listings",
      previousShortStay: "Show previous short-stay listings",
      nextShortStay: "Show next short-stay listings",
      viewAllShortStays: "View All Short Stays",
      exploreShortStays: "Explore Short Stays",
      exploreCars: "Explore Cars for Rent or Sale",
      carsEyebrow: "Cars · {count} available",
      carsHeading: "Cars for rent or sale",
      previousCars: "Show previous cars",
      nextCars: "Show next cars",
      viewAllCars: "View All Cars",
      noCarsAvailable: "No approved cars available yet.",
      contactEyebrow: "Contact Junub Homes",
      contactHeading: "Tell us what you need",
      name: "Name",
      phoneNumber: "Phone Number",
      subject: "Subject",
      message: "Message",
      namePlaceholder: "Your full name",
      phonePlaceholder: "Best number to reach you",
      subjectPlaceholder: "What can we help you with?",
      messagePlaceholder:
        "Tell us the property type, budget, location, or any question you have.",
      validationRequired:
        "Please fill in your name, phone number, subject, and message.",
      validationName: "Name must be 120 characters or less.",
      validationPhone:
        "Phone number must be 7 to 40 characters and use numbers, spaces, +, -, or brackets.",
      validationSubject: "Subject must be 160 characters or less.",
      validationMessage: "Message must be 2000 characters or less.",
      messageSent: "Thanks. Your message has been sent to the team.",
      messageFailed: "We could not send your message right now.",
      sending: "Sending...",
      sendMessage: "Send Message",
      sponsored: "Sponsored",
      featuredAdvert: "Featured Advert",
      advertisingSpace: "Advertising Space",
      viewAdvert: "View advert {number}",
      whyChooseUs: "Why Choose Us",
      trustedListings: "Trusted Listings",
      clearSearchTools: "Clear Search Tools",
      usefulDetails: "Useful Details",
      dismissMessage: "Dismiss message",
      categoryProperties: "Properties",
      categoryHouses: "Houses",
      categoryStores: "Warehouses / Stores",
      categoryPlural: "{type}s",
    },
    details: {
      notFound: "This property could not be found.",
      loadFailed: "We could not load this property right now.",
      loading: "Loading property details...",
      unavailable: "Property unavailable.",
      locationUnavailable: "Location unavailable",
      listingPrice: "Listing Price",
      imageView: "{title} view {number}",
      imageThumbnail: "{title} thumbnail {number}",
      previousImage: "Show previous property image",
      nextImage: "Show next property image",
      bedrooms: "Bedrooms",
      bathrooms: "Bathrooms",
      rooms: "Bedrooms",
      toilets: "Bathrooms",
      propertyType: "Property Type",
      propertyFallback: "Property",
      spaceSize: "Space Size in m²",
      cubicSize: "Size in m³",
      status: "Status",
      statusFallback: "pending",
      description: "Description",
      noDescription: "No description provided.",
      features: "Property Features",
      rules: "House Rules",
      shortStayHost: "Short Stay Host Details",
      name: "Name:",
      phone: "Phone:",
      email: "Email:",
      notProvided: "Not provided",
      requestListing: "Request This Listing",
      openingContact: "Opening Contact...",
      contactOpen: "Contact Details Open",
      contactAgent: "Contact Agent",
      contactSaved:
        "Contact details are now visible. The admin team has also received this request.",
      contactSaveFailed:
        "We could not save your request right now. You can still contact Junub Homes using the details below.",
    },
    propertyTypes: {
      Apartment: "Apartment",
      House: "House",
      "Commercial Building": "Commercial Building",
      Hotel: "Hotel",
      Office: "Office",
      Shop: "Shop",
      Store: "Warehouse / Store",
      Plot: "Plot of Land",
    },
    listingTypes: {
      "For Sale": "For Sale",
      "For Rent": "For Rent",
      "For Lease": "For Lease",
      "Short Stay": "Short Stay",
    },
  },
  ar: {
    language: {
      label: "اللغة",
      english: "English",
      arabic: "العربية",
    },
    nav: {
      home: "الرئيسية",
      shortStay: "Short Stay",
      addProperty: "إضافة عقار",
      privateListings: "القوائم الخاصة",
      about: "من نحن",
      services: "الخدمات",
      contact: "اتصل بنا",
      admin: "الإدارة",
      logout: "تسجيل الخروج",
      login: "تسجيل الدخول",
      loggedOut: "تم تسجيل الخروج بنجاح",
      toggleMenu: "فتح أو إغلاق قائمة التنقل",
    },
    home: {
      heroDefault: "اعثر على عقارك القادم",
      heroListing: "عقارات {listingType}",
      searchPlaceholder: "ابحث بالعنوان أو الموقع أو كلمة مفتاحية",
      mobileSearchPlaceholder: "ابحث بالموقع أو العنوان...",
      minPrice: "أقل سعر",
      maxPrice: "أعلى سعر",
      rooms: "الغرف",
      propertyType: "نوع العقار",
      listingType: "نوع العرض",
      clearFilters: "مسح الفلاتر",
      resetFilters: "إعادة ضبط الفلاتر",
      filters: "الفلاتر",
      filterProperties: "تصفية العقارات",
      browseByCategory: "تصفح حسب الفئة",
      availableProperties: "العقارات المتاحة",
      availableCategory: "{category} المتاحة",
      loadingListings: "جاري تحميل القوائم...",
      approvedListingsFound: "تم العثور على {count} قائمة معتمدة",
      loading: "جاري التحميل...",
      listingsReady: "{count} قائمة جاهزة",
      allListingTypes: "كل أنواع العروض",
      allPropertyTypes: "كل أنواع العقارات",
      loadingApproved: "جاري تحميل العقارات المعتمدة...",
      loadFailed: "تعذر تحميل القوائم المعتمدة الآن.",
      noMatches: "لا توجد عقارات تطابق البحث والفلاتر الحالية.",
      reset: "إعادة ضبط",
      shortStayCount: "إقامة قصيرة · {count} قائمة",
      quickStays: "إقامات قصيرة جاهزة للحجز",
      noShortStays: "لا توجد قوائم إقامة قصيرة متاحة حاليا.",
      previousShortStay: "عرض قوائم الإقامة القصيرة السابقة",
      nextShortStay: "عرض قوائم الإقامة القصيرة التالية",
      viewAllShortStays: "عرض كل الإقامات القصيرة",
      contactEyebrow: "اتصل بـ Junub Homes",
      contactHeading: "أخبرنا بما تحتاجه",
      name: "الاسم",
      phoneNumber: "رقم الهاتف",
      subject: "الموضوع",
      message: "الرسالة",
      namePlaceholder: "اسمك الكامل",
      phonePlaceholder: "أفضل رقم للتواصل معك",
      subjectPlaceholder: "كيف يمكننا مساعدتك؟",
      messagePlaceholder:
        "اكتب نوع العقار أو الميزانية أو الموقع أو أي سؤال لديك.",
      validationRequired:
        "يرجى تعبئة الاسم ورقم الهاتف والموضوع والرسالة.",
      validationName: "يجب ألا يتجاوز الاسم 120 حرفا.",
      validationPhone:
        "يجب أن يتكون رقم الهاتف من 7 إلى 40 حرفا ويستخدم الأرقام والمسافات و + و - والأقواس.",
      validationSubject: "يجب ألا يتجاوز الموضوع 160 حرفا.",
      validationMessage: "يجب ألا تتجاوز الرسالة 2000 حرف.",
      messageSent: "شكرا لك. تم إرسال رسالتك إلى الفريق.",
      messageFailed: "تعذر إرسال رسالتك الآن.",
      sending: "جاري الإرسال...",
      sendMessage: "إرسال الرسالة",
      sponsored: "إعلان ممول",
      featuredAdvert: "إعلان مميز",
      advertisingSpace: "مساحة إعلانية",
      viewAdvert: "عرض الإعلان {number}",
      whyChooseUs: "لماذا تختارنا",
      trustedListings: "قوائم موثوقة",
      clearSearchTools: "أدوات بحث واضحة",
      usefulDetails: "تفاصيل مفيدة",
      dismissMessage: "إغلاق الرسالة",
      categoryProperties: "العقارات",
      categoryHouses: "المنازل",
      categoryStores: "المخازن / المستودعات",
      categoryPlural: "{type}",
    },
    details: {
      notFound: "تعذر العثور على هذا العقار.",
      loadFailed: "تعذر تحميل هذا العقار الآن.",
      loading: "جاري تحميل تفاصيل العقار...",
      unavailable: "العقار غير متاح.",
      locationUnavailable: "الموقع غير متاح",
      listingPrice: "سعر العرض",
      imageView: "{title} صورة {number}",
      imageThumbnail: "{title} صورة مصغرة {number}",
      previousImage: "عرض صورة العقار السابقة",
      nextImage: "عرض صورة العقار التالية",
      rooms: "الغرف",
      toilets: "الحمامات",
      propertyType: "نوع العقار",
      propertyFallback: "عقار",
      spaceSize: "المساحة بالمتر المربع",
      cubicSize: "الحجم بالمتر المكعب",
      status: "الحالة",
      statusFallback: "قيد الانتظار",
      description: "الوصف",
      noDescription: "لا يوجد وصف.",
      features: "مميزات العقار",
      rules: "قواعد السكن",
      shortStayHost: "تفاصيل مضيف الإقامة القصيرة",
      name: "الاسم:",
      phone: "الهاتف:",
      email: "البريد الإلكتروني:",
      notProvided: "غير متوفر",
      requestListing: "طلب هذا العرض",
      openingContact: "جاري فتح جهة الاتصال...",
      contactOpen: "تم فتح تفاصيل الاتصال",
      contactAgent: "اتصل بالوكيل",
      contactSaved:
        "تفاصيل الاتصال ظاهرة الآن. تم إرسال هذا الطلب أيضا إلى فريق الإدارة.",
      contactSaveFailed:
        "تعذر حفظ طلبك الآن. لا يزال بإمكانك التواصل مع Junub Homes باستخدام التفاصيل أدناه.",
    },
    propertyTypes: {
      Apartment: "شقة",
      House: "منزل",
      Hotel: "فندق",
      Office: "مكتب",
      Shop: "محل",
      Store: "مستودع / مخزن",
      Plot: "قطعة أرض",
    },
      listingTypes: {
        "For Sale": "للبيع",
        "For Rent": "للإيجار",
        "For Lease": "للإيجار بعقد",
        "Short Stay": "إقامة قصيرة",
      },
  },
};

Object.assign(TRANSLATIONS.ar.home, {
  rooms: "غرف النوم",
});

Object.assign(TRANSLATIONS.ar.details, {
  bedrooms: "غرف النوم",
  bathrooms: "الحمامات",
  rooms: "غرف النوم",
  toilets: "الحمامات",
});

Object.assign(TRANSLATIONS.ar.propertyTypes, {
  "Commercial Building": "مبنى تجاري",
});

Object.assign(TRANSLATIONS.en.home, {
  exploreShortStays: "Explore Short Stays",
  exploreCars: "Explore Cars for Rent or Sale",
  carsEyebrow: "Cars · {count} available",
  carsHeading: "Cars for rent or sale",
  viewAllCars: "View All Cars",
  noCarsAvailable: "No approved cars available yet.",
});

Object.assign(TRANSLATIONS.ar.home, {
  exploreShortStays: "استكشف الإقامات القصيرة",
  exploreCars: "استكشف السيارات للإيجار أو البيع",
  carsEyebrow: "السيارات · {count} متاحة",
  carsHeading: "سيارات للإيجار أو البيع",
  viewAllCars: "عرض كل السيارات",
  noCarsAvailable: "لا توجد سيارات معتمدة متاحة حتى الآن.",
});

STATIC_TRANSLATIONS.zh = {
  Home: "首页",
  Explore: "浏览",
  Company: "公司",
  About: "关于我们",
  Services: "服务",
  Contact: "联系我们",
  Cars: "车辆",
  "For Rent": "出租",
  "For Sale": "出售",
  "For Lease": "租赁",
  "Privacy Policy": "隐私政策",
  "All rights reserved.": "版权所有。",
  Nairobi: "内罗毕",
  "Nairobi, Kenya": "肯尼亚内罗毕",
  "Location unavailable": "位置不可用",
  "Location details available on request": "可按要求提供位置详情",
  "Terms and Conditions": "条款与条件",
  "Effective date:": "生效日期：",
  "Last updated:": "最后更新：",
  USD: "美元",
  KES: "肯尼亚先令",
};

STATIC_TRANSLATIONS.so = {
  Home: "Bogga Hore",
  Explore: "Saham",
  Company: "Shirkad",
  About: "Nagu Saabsan",
  Services: "Adeegyada",
  Contact: "Nala Soo Xiriir",
  Cars: "Baabuurta",
  "For Rent": "Kiro",
  "For Sale": "Iib",
  "For Lease": "Liiis",
  "Privacy Policy": "Siyaasadda Asturnaanta",
  "All rights reserved.": "Dhammaan xuquuqdu way dhowran tahay.",
  Nairobi: "Nairobi",
  "Nairobi, Kenya": "Nairobi, Kenya",
  "Location unavailable": "Goob lama heli karo",
  "Location details available on request": "Faahfaahinta goobta codsi ayaa lagu heli karaa",
  "Terms and Conditions": "Shuruudaha iyo Xaaladaha",
  "Effective date:": "Taariikhda dhaqan-galka:",
  "Last updated:": "Markii u dambeysay ee la cusbooneysiiyay:",
  USD: "Doolarka Mareykanka",
  KES: "Shilingka Kenya",
};

Object.assign(STATIC_TRANSLATIONS.ar, {
  About: "من نحن",
  Company: "الشركة",
  Explore: "استكشف",
  Hotels: "الفنادق",
  Adverts: "الإعلانات",
  Agent: "الوكيل",
  Approve: "اعتماد",
  Approved: "معتمد",
  "Assigned agent": "الوكيل المعين",
  Automatic: "أوتوماتيك",
  Back: "رجوع",
  Body: "الهيكل",
  Budget: "الميزانية",
  Cancel: "إلغاء",
  "Car brand": "ماركة السيارة",
  "Car model": "موديل السيارة",
  "Car listing submitted, but {count} image(s) failed to upload.":
    "تم إرسال إعلان السيارة، لكن فشل رفع {count} صورة.",
  Close: "إغلاق",
  Color: "اللون",
  "Confirm password": "تأكيد كلمة المرور",
  Created: "تاريخ الإنشاء",
  Currency: "العملة",
  Dates: "التواريخ",
  Delete: "حذف",
  Destination: "الوجهة",
  Diesel: "ديزل",
  Drive: "الدفع",
  Edit: "تعديل",
  Electric: "كهربائي",
  Email: "البريد الإلكتروني",
  Engine: "المحرك",
  "Enter Space Size in m²": "أدخل المساحة بالمتر المربع",
  "Enter a valid year of manufacture.": "أدخل سنة تصنيع صحيحة.",
  "Enter your current password to update secure details.":
    "أدخل كلمة المرور الحالية لتحديث البيانات الآمنة.",
  "Enter your profile name.": "أدخل اسم ملفك الشخصي.",
  Expires: "ينتهي",
  Features: "المميزات",
  "Filter cars by name, status, location, or type":
    "تصفية السيارات حسب الاسم أو الحالة أو الموقع أو النوع",
  "Filter properties by title, status, location, or type":
    "تصفية العقارات حسب العنوان أو الحالة أو الموقع أو النوع",
  Fuel: "الوقود",
  Guest: "ضيف",
  Guests: "الضيوف",
  "Hotel Requests": "طلبات الفنادق",
  "Hotel Service Requests": "طلبات خدمة الفنادق",
  "Hotel request": "طلب فندق",
  Hybrid: "هجين",
  "Junub Homes admin": "إدارة Junub Homes",
  "Loading dashboard data...": "جارٍ تحميل بيانات لوحة الإدارة...",
  "Loading your dashboard...": "جارٍ تحميل لوحتك...",
  "Location not provided": "الموقع غير متوفر",
  Location: "الموقع",
  Manual: "عادي",
  "Mark {status}": "تحديد كـ {status}",
  Messages: "الرسائل",
  "Mileage cannot be negative.": "لا يمكن أن تكون المسافة المقطوعة سالبة.",
  Mileage: "المسافة المقطوعة",
  Negotiable: "قابل للتفاوض",
  "New password": "كلمة المرور الجديدة",
  "No add-ons requested": "لم يتم طلب إضافات",
  "No cars match the current filters.": "لا توجد سيارات تطابق الفلاتر الحالية.",
  "No cars match your filter": "لا توجد سيارات تطابق الفلتر",
  "No contact requests match this filter.":
    "لا توجد طلبات تواصل تطابق هذا الفلتر.",
  "No hotel service requests have been received yet.":
    "لم يتم استلام أي طلبات خدمة فنادق بعد.",
  "No hotel service requests match this filter.":
    "لا توجد طلبات خدمة فنادق تطابق هذا الفلتر.",
  "No listing contact requests have been received yet.":
    "لم يتم استلام أي طلبات تواصل مع الإعلانات بعد.",
  "No properties match your filter": "لا توجد عقارات تطابق الفلتر",
  No: "لا",
  "Not updated": "غير محدّث",
  Notes: "ملاحظات",
  Open: "فتح",
  "Open budget": "ميزانية مفتوحة",
  Optional: "اختياري",
  Organization: "الجهة",
  Owner: "المالك",
  "Owner details": "تفاصيل المالك",
  Password: "كلمة المرور",
  Payment: "الدفع",
  Pending: "قيد الانتظار",
  Petrol: "بنزين",
  Phone: "الهاتف",
  "Please log in before adding a car listing.":
    "يرجى تسجيل الدخول قبل إضافة إعلان سيارة.",
  "Please sign in again before uploading files.":
    "يرجى تسجيل الدخول مرة أخرى قبل رفع الملفات.",
  Price: "السعر",
  Profile: "الملف الشخصي",
  Reactivate: "إعادة التفعيل",
  Received: "تم الاستلام",
  Recently: "مؤخرًا",
  Reject: "رفض",
  Rejected: "مرفوض",
  Remove: "إزالة",
  "Request new link": "اطلب رابطًا جديدًا",
  Requester: "مقدم الطلب",
  "Requester ID": "معرّف مقدم الطلب",
  Role: "الدور",
  Rooms: "الغرف",
  Rule: "قاعدة",
  Rules: "القواعد",
  "Sale type": "نوع البيع",
  Save: "حفظ",
  "Search car, location, body type": "ابحث بالسيارة أو الموقع أو نوع الهيكل",
  "Search title or location": "ابحث بالعنوان أو الموقع",
  Security: "الأمان",
  "Select body type": "اختر نوع الهيكل",
  "Select drive type": "اختر نوع الدفع",
  "Select engine size": "اختر حجم المحرك",
  "Select fuel type": "اختر نوع الوقود",
  "Select transmission": "اختر ناقل الحركة",
  "Select {label}": "اختر {label}",
  Size: "الحجم",
  Subscriptions: "الاشتراكات",
  Suspend: "إيقاف",
  Title: "العنوان",
  Transmission: "ناقل الحركة",
  "Transmission is required.": "ناقل الحركة مطلوب.",
  Unavailable: "غير متاح",
  "Untitled car": "سيارة بدون عنوان",
  Updated: "تم التحديث",
  "Uploading...": "جارٍ الرفع...",
  Users: "المستخدمون",
  Utilities: "الخدمات",
  "We could not load cars right now.": "تعذر تحميل السيارات الآن.",
  "We could not load hotel listings right now.":
    "تعذر تحميل قوائم الفنادق الآن.",
  WhatsApp: "واتساب",
  "WhatsApp not provided": "واتساب غير متوفر",
  Year: "السنة",
  Yes: "نعم",
  "Your admin session has expired. Please log in again.":
    "انتهت جلسة الإدارة. يرجى تسجيل الدخول مرة أخرى.",
  "Your session has expired. Please log in again.":
    "انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.",
  new: "جديد",
  read: "مقروء",
  unread: "غير مقروء",
  "{count} cars matched": "{count} سيارة مطابقة",
  "{count}% uploaded": "تم رفع {count}%",
  "Properties and car rentals, sales, and listing support in Nairobi. Contact us to list your property, or explore our website when you are ready to find your next home, space, or ride.":
    "العقارات وإيجار السيارات وبيعها ودعم الإعلانات في نيروبي. تواصل معنا لإدراج عقارك، أو تصفح موقعنا عندما تكون جاهزًا للعثور على منزلك أو مساحتك أو وسيلة تنقلك التالية.",
  "Location details available on request": "تفاصيل الموقع متاحة عند الطلب",
  "Footer navigation": "تنقل التذييل",
  "Upload at least three images.": "ارفع ثلاث صور على الأقل.",
  "Add Home, Plot, Apartment etc": "أضف منزلًا أو قطعة أرض أو شقة وغير ذلك",
  "Add car pictures": "أضف صور السيارة",
  "Add-ons": "الإضافات",
  "Agent email not provided": "بريد الوكيل غير متوفر",
});

Object.assign(STATIC_TRANSLATIONS.so, {
  About: "Nagu Saabsan",
  Company: "Shirkad",
  Explore: "Saham",
  Hotels: "Hudheelada",
  Adverts: "Xayeysiisyada",
  Agent: "Wakiil",
  Approve: "Ansixi",
  Approved: "La ansixiyey",
  "Assigned agent": "Wakiilka loo xilsaaray",
  Automatic: "Otomaatig",
  Back: "Dib u noqo",
  Body: "Jirka",
  Budget: "Miisaaniyad",
  Cancel: "Jooji",
  "Car brand": "Summadda baabuurka",
  "Car model": "Moodelka baabuurka",
  "Car listing submitted, but {count} image(s) failed to upload.":
    "Liiska baabuurka waa la gudbiyey, laakiin {count} sawir ma uusan soo gelin.",
  Close: "Xir",
  Color: "Midabka",
  "Confirm password": "Xaqiiji erayga sirta",
  Created: "La sameeyey",
  Currency: "Lacagta",
  Dates: "Taariikhaha",
  Delete: "Tirtir",
  Destination: "Meesha loo socdo",
  Diesel: "Diesel",
  Drive: "Jiidadka",
  Edit: "Wax ka beddel",
  Electric: "Koronto",
  Email: "Iimayl",
  Engine: "Matoorka",
  "Enter Space Size in m²": "Geli baaxadda goobta ee m²",
  "Enter a valid year of manufacture.": "Geli sannad soo saar sax ah.",
  "Enter your current password to update secure details.":
    "Geli eraygaaga sirta ee hadda si aad u cusboonaysiiso xogta amniga.",
  "Enter your profile name.": "Geli magaca profile-kaaga.",
  Expires: "Wuu dhacayaa",
  Features: "Astaamo",
  "Filter cars by name, status, location, or type":
    "Ku shaandhee baabuurta magac, xaalad, goob, ama nooc",
  "Filter properties by title, status, location, or type":
    "Ku shaandhee guryaha cinwaan, xaalad, goob, ama nooc",
  Fuel: "Shidaal",
  Guest: "Marti",
  Guests: "Martida",
  "Hotel Requests": "Codsiyada hudheelka",
  "Hotel Service Requests": "Codsiyada adeegga hudheelka",
  "Hotel request": "Codsi hudheel",
  Hybrid: "Hybrid",
  "Junub Homes admin": "Maamulka Junub Homes",
  "Loading dashboard data...": "Xogta dashboard-ka waa la soo gelinayaa...",
  "Loading your dashboard...": "Dashboard-kaaga waa la soo gelinayaa...",
  "Location not provided": "Goob lama bixin",
  Location: "Goobta",
  Manual: "Gacanta",
  "Mark {status}": "U calaamadee {status}",
  Messages: "Farriimaha",
  "Mileage cannot be negative.": "Masaafadu ma noqon karto taban.",
  Mileage: "Masaafada",
  Negotiable: "La gorgortami karo",
  "New password": "Eray sir cusub",
  "No add-ons requested": "Waxyaabo dheeri ah lama codsan",
  "No cars match the current filters.": "Ma jiro baabuur la jaanqaadaya shaandhooyinka hadda.",
  "No cars match your filter": "Ma jiro baabuur la jaanqaadaya shaandhadaada",
  "No contact requests match this filter.":
    "Ma jiraan codsiyo xiriir oo la jaanqaadaya shaandhadan.",
  "No hotel service requests have been received yet.":
    "Weli lama helin codsiyo adeeg hudheel.",
  "No hotel service requests match this filter.":
    "Ma jiraan codsiyo adeeg hudheel oo la jaanqaadaya shaandhadan.",
  "No listing contact requests have been received yet.":
    "Weli lama helin codsiyo xiriir liis.",
  "No properties match your filter": "Ma jiro guri la jaanqaadaya shaandhadaada",
  No: "Maya",
  "Not updated": "Lama cusboonaysiin",
  Notes: "Qoraallo",
  Open: "Fur",
  "Open budget": "Miisaaniyad furan",
  Optional: "Ikhtiyaari",
  Organization: "Hay'ad",
  Owner: "Milkiile",
  "Owner details": "Faahfaahinta milkiilaha",
  Password: "Erayga sirta",
  Payment: "Lacag bixin",
  Pending: "Sugaya",
  Petrol: "Batrool",
  Phone: "Telefoon",
  "Please log in before adding a car listing.":
    "Fadlan soo gal ka hor intaadan darin liiska baabuurka.",
  "Please sign in again before uploading files.":
    "Fadlan mar kale soo gal ka hor intaadan gelin faylasha.",
  Price: "Qiimaha",
  Profile: "Profile",
  Reactivate: "Dib u hawlgeli",
  Received: "La helay",
  Recently: "Dhawaan",
  Reject: "Diid",
  Rejected: "La diiday",
  Remove: "Ka saar",
  "Request new link": "Codso xiriiriye cusub",
  Requester: "Codsade",
  "Requester ID": "Aqoonsiga codsadaha",
  Role: "Doorka",
  Rooms: "Qolalka",
  Rule: "Xeer",
  Rules: "Xeerarka",
  "Sale type": "Nooca iibka",
  Save: "Kaydi",
  "Search car, location, body type": "Raadi baabuur, goob, ama nooca jirka",
  "Search title or location": "Raadi cinwaan ama goob",
  Security: "Amni",
  "Select body type": "Dooro nooca jirka",
  "Select drive type": "Dooro nooca jiidista",
  "Select engine size": "Dooro cabbirka matoorka",
  "Select fuel type": "Dooro nooca shidaalka",
  "Select transmission": "Dooro transmission-ka",
  "Select {label}": "Dooro {label}",
  Size: "Cabbirka",
  Subscriptions: "Diiwaangelinnada",
  Suspend: "Hakinta",
  Title: "Cinwaan",
  Transmission: "Transmission",
  "Transmission is required.": "Transmission waa loo baahan yahay.",
  Unavailable: "Lama heli karo",
  "Untitled car": "Baabuur aan cinwaan lahayn",
  Updated: "La cusboonaysiiyey",
  "Uploading...": "Waa la gelinayaa...",
  Users: "Isticmaaleyaasha",
  Utilities: "Adeegyada",
  "We could not load cars right now.": "Hadda ma soo dejin karno baabuurta.",
  "We could not load hotel listings right now.":
    "Hadda ma soo dejin karno liisaska hudheelada.",
  WhatsApp: "WhatsApp",
  "WhatsApp not provided": "WhatsApp lama bixin",
  Year: "Sannad",
  Yes: "Haa",
  "Your admin session has expired. Please log in again.":
    "Kulankaaga maamulka wuu dhacay. Fadlan mar kale soo gal.",
  "Your session has expired. Please log in again.":
    "Kulankaaga wuu dhacay. Fadlan mar kale soo gal.",
  new: "cusub",
  read: "la akhriyey",
  unread: "aan la akhrin",
  "{count} cars matched": "{count} baabuur ayaa la helay",
  "{count}% uploaded": "{count}% waa la geliyey",
  "Properties and car rentals, sales, and listing support in Nairobi. Contact us to list your property, or explore our website when you are ready to find your next home, space, or ride.":
    "Guryo, kirada baabuurta, iibka, iyo taageerada liisaska ee Nairobi. Nala soo xiriir si aad u darto hantidaada, ama sahami websaytkayaga markaad diyaar u tahay inaad hesho gurigaaga, goobtaada, ama gaadhigaaga xiga.",
  "Footer navigation": "Hagaha hoose",
  "Upload at least three images.": "Soo geli ugu yaraan saddex sawir.",
  "Add Home, Plot, Apartment etc": "Ku dar guri, dhul, apartment iyo wixii la mid ah",
  "Add car pictures": "Ku dar sawirrada baabuurka",
  "Add-ons": "Waxyaabo dheeri ah",
  "Agent email not provided": "Iimaylka wakiilka lama bixin",
});

TRANSLATIONS.zh = {
  ...TRANSLATIONS.en,
  language: {
    label: "语言",
    english: "English",
    arabic: "العربية",
    chinese: "中文",
    somali: "Soomaali",
  },
  nav: {
    ...TRANSLATIONS.en.nav,
    cars: "Cars",
  },
};

TRANSLATIONS.so = {
  ...TRANSLATIONS.en,
  language: {
    label: "Luqad",
    english: "English",
    arabic: "العربية",
    chinese: "中文",
    somali: "Soomaali",
  },
  nav: {
    ...TRANSLATIONS.en.nav,
    cars: "Cars",
  },
};

Object.assign(STATIC_TRANSLATIONS.zh, {
  Hotels: "é…’åº—",
  "Short Stay": "çŸ­ä½",
  "Footer navigation": "é¡µè„šå¯¼èˆª",
  "About Junub Homes": "å…³äºŽ Junub Homes",
  "A focused property platform for Nairobi.": "ä¸“æ³¨äºŽæœ±å·´çš„æˆ¿äº§å¹³å°ã€‚",
  "What We Do": "æˆ‘ä»¬çš„æœåŠ¡",
  "We make property discovery simpler and more organized.":
    "æˆ‘ä»¬è®©æˆ¿äº§æœç´¢æ›´ç®€å•ã€æ›´æœ‰æ¡ç†ã€‚",
  "Our values": "æˆ‘ä»¬çš„ä»·å€¼è§‚",
  "Our Promise": "æˆ‘ä»¬çš„æ‰¿è¯º",
  "Practical tools for listing and finding properties and cars.":
    "ç”¨äºŽå‘å¸ƒå’ŒæŸ¥æ‰¾æˆ¿äº§ä¸Žè½¦è¾†çš„å®žç”¨å·¥å…·ã€‚",
  "Ready to browse live listings?": "å‡†å¤‡å¥½æŸ¥çœ‹åœ¨çº¿æˆ¿æºäº†å—ï¼Ÿ",
  "Find the right car for work, travel, rent, or ownership.":
    "ä¸ºå·¥ä½œã€�å‡ºè¡Œã€�ç§Ÿèµæˆ–è´­ä¹°æ‰¾åˆ°åˆé€‚çš„è½¦ã€‚",
  "Search brand, model, location": "æœç´¢å“ç‰Œã€�è½¦åž‹ã€�ä½ç½®",
  "Rent or sale": "ç§Ÿèµæˆ–å‡ºå”®",
  "Body type": "è½¦èº«ç±»åž‹",
  Transmission: "å˜é€Ÿç®±",
  Fuel: "ç‡ƒæ–™",
  Petrol: "æ±½æ²¹",
  Diesel: "æŸ´æ²¹",
  Hybrid: "æ··åˆåŠ¨åŠ›",
  Electric: "ç”µåŠ¨",
  Automatic: "è‡ªåŠ¨",
  Manual: "æ‰‹åŠ¨",
  "Max price": "æœ€é«˜ä»·æ ¼",
  Reset: "é‡ç½®",
  "Featured Cars": "ç²¾é€‰è½¦è¾†",
  "{count} available": "å¯ç”¨ {count} è¾†",
  "Loading cars...": "æ­£åœ¨åŠ è½½è½¦è¾†...",
  "No approved cars match your filters.":
    "æ²¡æœ‰ç¬¦åˆç­›é€‰æ¡ä»¶çš„å·²å®¡æ ¸è½¦è¾†ã€‚",
  "Why choose us": "ä¸ºä»€ä¹ˆé€‰æ‹©æˆ‘ä»¬",
  "Reviewed listings": "å·²å®¡æ ¸åˆ—è¡¨",
  "Direct contacts": "ç›´æŽ¥è”ç³»",
  "Built for teams": "ä¸ºå›¢é˜Ÿè®¾è®¡",
  "Hotels, corporate stays, and concierge support for Nairobi.":
    "ä¸ºæœ±å·´æä¾›é…’åº—ã€�å•†åŠ¡ä½å®¿å’Œç®¡å®¶æ”¯æŒã€‚",
  "View Hotels": "æŸ¥çœ‹é…’åº—",
  "Request Hotel Support": "ç”³è¯·é…’åº—æ”¯æŒ",
  "Live hotel listings": "åœ¨çº¿é…’åº—åˆ—è¡¨",
  "Search hotel name or area": "æœç´¢é…’åº—åç§°æˆ–åŒºåŸŸ",
  "Max nightly budget (USD)": "æ¯æ™šæœ€é«˜é¢„ç®—ï¼ˆUSDï¼‰",
  "Loading hotels...": "æ­£åœ¨åŠ è½½é…’åº—...",
  "No approved hotels match the current filters.":
    "æ²¡æœ‰ç¬¦åˆå½“å‰ç­›é€‰æ¡ä»¶çš„å·²å®¡æ ¸é…’åº—ã€‚",
  "Request this hotel": "è¯·æ±‚è¯¥é…’åº—",
  "WhatsApp hotel": "WhatsApp è”ç³»é…’åº—",
  "Direct phone not available yet": "ç›´æ‹¨ç”µè¯æš‚æœªæä¾›",
  "Find your next home or business premises from the comfort of your home.":
    "è¶³ä¸å‡ºæˆ·å³å¯å¯»æ‰¾æ‚¨çš„ä¸‹ä¸€å¤„ä½å®…æˆ–å•†ä¸šåœºæ‰€ã€‚",
  "Search by title, location, or keyword": "æŒ‰æ ‡é¢˜ã€�ä½ç½®æˆ–å…³é”®è¯æœç´¢",
  "Search location or title...": "æœç´¢ä½ç½®æˆ–æ ‡é¢˜...",
  "Min Price": "æœ€ä½Žä»·æ ¼",
  "Max Price": "æœ€é«˜ä»·æ ¼",
  "All listing types": "æ‰€æœ‰åˆ—è¡¨ç±»åž‹",
  "All property types": "æ‰€æœ‰æˆ¿äº§ç±»åž‹",
  "Clear Filters": "æ¸…é™¤ç­›é€‰",
  "Reset Filters": "é‡ç½®ç­›é€‰",
  Filters: "ç­›é€‰",
  "Browse by Category": "æŒ‰ç±»åˆ«æµè§ˆ",
  "Loading listings...": "æ­£åœ¨åŠ è½½åˆ—è¡¨...",
  "{count} approved listing(s) found": "å·²æ‰¾åˆ° {count} æ¡å·²å®¡æ ¸ä¿¡æ¯",
  "Loading approved properties...": "æ­£åœ¨åŠ è½½å·²å®¡æ ¸æˆ¿æº...",
  "We could not load approved listings right now.":
    "å½“å‰æ— æ³•åŠ è½½å·²å®¡æ ¸åˆ—è¡¨ã€‚",
  "No properties match the current search and filters.":
    "æ²¡æœ‰ç¬¦åˆå½“å‰æœç´¢å’Œç­›é€‰æ¡ä»¶çš„æˆ¿æºã€‚",
  "View All Short Stays": "æŸ¥çœ‹æ‰€æœ‰çŸ­ä½",
  "Explore Cars for Rent or Sale": "æµè§ˆå¯ç§Ÿæˆ–å¯å”®çš„è½¦è¾†",
  "Tell us what you need": "å‘Šè¯‰æˆ‘ä»¬æ‚¨çš„éœ€æ±‚",
  "What can we help you with?": "æˆ‘ä»¬å¯ä»¥å¦‚ä½•å¸®æ‚¨ï¼Ÿ",
  "Tell us the property type, budget, location, or any question you have.":
    "è¯·å‘Šè¯‰æˆ‘ä»¬æ‚¨éœ€è¦çš„æˆ¿äº§ç±»åž‹ã€�é¢„ç®—ã€�ä½ç½®æˆ–ä»»ä½•é—®é¢˜ã€‚",
  "Featured Advert": "ç²¾é€‰å¹¿å‘Š",
  "Advertising Space": "å¹¿å‘Šä½",
});

Object.assign(STATIC_TRANSLATIONS.so, {
  Hotels: "Hudheellada",
  "Short Stay": "Joogitaan Gaaban",
  "Footer navigation": "Hagaha hoose",
  "About Junub Homes": "Ku saabsan Junub Homes",
  "A focused property platform for Nairobi.": "Madal hantiyeed si gaar ah ugu adeegta Nairobi.",
  "What We Do": "Waxaan Qabanno",
  "We make property discovery simpler and more organized.":
    "Waxaan ka dhignaa helidda guryaha mid fudud oo habaysan.",
  "Our values": "Qiyamkayaga",
  "Our Promise": "Ballanqaadkayaga",
  "Practical tools for listing and finding properties and cars.":
    "Qalab wax ku ool ah oo lagu soo bandhigo laguna helo guryo iyo baabuur.",
  "Ready to browse live listings?": "Diyaar ma u tahay inaad eegto xayaysiisyada tooska ah?",
  "Find the right car for work, travel, rent, or ownership.":
    "Hel baabuurka saxda ah ee shaqo, safar, kiro, ama lahaansho.",
  "Search brand, model, location": "Raadi nooc, moodal, ama goob",
  "Rent or sale": "Kiro ama iib",
  "Body type": "Nooca jirka",
  Transmission: "Gudbin",
  Fuel: "Shidaal",
  Petrol: "Baatrool",
  Diesel: "Naafto",
  Hybrid: "Isku-dhafan",
  Electric: "Koronto",
  Automatic: "Otomaatig",
  Manual: "Gacanta",
  "Max price": "Qiimaha ugu badan",
  Reset: "Dib u celi",
  "Featured Cars": "Baabuurta la xushay",
  "{count} available": "{count} ayaa diyaar ah",
  "Loading cars...": "Baabuurta waa la soo gelinayaa...",
  "No approved cars match your filters.":
    "Ma jiraan baabuur la ansixiyey oo ku habboon shaandhooyinkaaga.",
  "Why choose us": "Maxaad noo dooranaysaa",
  "Reviewed listings": "Xayaysiisyada la hubiyey",
  "Direct contacts": "Xiriir toos ah",
  "Built for teams": "Loogu dhisay kooxo",
  "Hotels, corporate stays, and concierge support for Nairobi.":
    "Hudheello, deganaansho shirkadeed, iyo taageero adeeg gaar ah oo Nairobi ah.",
  "View Hotels": "Eeg hudheellada",
  "Request Hotel Support": "Codso taageero hudheel",
  "Live hotel listings": "Liisaska hudheelada tooska ah",
  "Search hotel name or area": "Raadi magaca hudheelka ama aagga",
  "Max nightly budget (USD)": "Miisaaniyadda ugu badan habeenkii (USD)",
  "Loading hotels...": "Hudheellada waa la soo gelinayaa...",
  "No approved hotels match the current filters.":
    "Ma jiraan hudheello la ansixiyey oo ku habboon shaandhooyinka hadda jira.",
  "Request this hotel": "Codso hudheelkan",
  "WhatsApp hotel": "Hudheelka WhatsApp",
  "Direct phone not available yet": "Lambarka tooska ah weli lama helin",
  "Find your next home or business premises from the comfort of your home.":
    "Ka hel gurigaaga dhexdiisa gurigaaga xiga ama goobtaada ganacsi.",
  "Search by title, location, or keyword": "Ku raadi cinwaan, goob, ama erey muhiim ah",
  "Search location or title...": "Raadi goob ama cinwaan...",
  "Min Price": "Qiimaha ugu yar",
  "Max Price": "Qiimaha ugu badan",
  "All listing types": "Dhammaan noocyada liisaska",
  "All property types": "Dhammaan noocyada hantida",
  "Clear Filters": "Nadiifi shaandhooyinka",
  "Reset Filters": "Dib u deji shaandhooyinka",
  Filters: "Shaandhooyin",
  "Browse by Category": "Ku eeg qayb ahaan",
  "Loading listings...": "Liisaska waa la soo gelinayaa...",
  "{count} approved listing(s) found": "{count} liis la ansixiyey ayaa la helay",
  "Loading approved properties...": "Guryaha la ansixiyey waa la soo gelinayaa...",
  "We could not load approved listings right now.":
    "Hadda ma soo gelin karno liisaska la ansixiyey.",
  "No properties match the current search and filters.":
    "Ma jiraan guryo ku habboon raadinta iyo shaandhooyinka hadda jira.",
  "View All Short Stays": "Eeg dhammaan joogitaanka gaaban",
  "Explore Cars for Rent or Sale": "Saham baabuurta kirada ama iibka ah",
  "Tell us what you need": "Noo sheeg waxaad u baahan tahay",
  "What can we help you with?": "Maxaan kaa caawin karnaa?",
  "Tell us the property type, budget, location, or any question you have.":
    "Noo sheeg nooca guri, miisaaniyadda, goobta, ama su'aal kasta oo aad qabto.",
  "Featured Advert": "Xayeysiis la xushay",
  "Advertising Space": "Goob xayeysiis",
});

Object.assign(TRANSLATIONS.zh.propertyTypes, {
  Apartment: "å…¬å¯“",
  House: "æˆ¿å±‹",
  Hotel: "é…’åº—",
  Office: "åŠžå…¬å®¤",
  Shop: "å•†é“º",
  Store: "ä»“åº“ / å•†åº—",
  Plot: "åœŸåœ°",
});
Object.assign(TRANSLATIONS.so.propertyTypes, {
  Apartment: "Guri dabaq ah",
  House: "Guri",
  Hotel: "Hudheel",
  Office: "Xafiis",
  Shop: "Dukaan",
  Store: "Bakhaar / Dukaan",
  Plot: "Dhul",
  "Commercial Building": "Dhisme Ganacsi",
});

Object.assign(TRANSLATIONS.zh.listingTypes, {
  "For Sale": "å‡ºå”®",
  "For Rent": "å‡ºç§Ÿ",
  "For Lease": "ç§Ÿèµ",
  "Short Stay": "çŸ­ä½",
});
Object.assign(TRANSLATIONS.so.listingTypes, {
  "For Sale": "Iib",
  "For Rent": "Kiro",
  "For Lease": "Liiis",
  "Short Stay": "Joogitaan Gaaban",
});

Object.assign(TRANSLATIONS.en.language, {
  mandarin: "Mandarin",
});
delete TRANSLATIONS.en.language.chinese;
delete TRANSLATIONS.en.language.somali;

Object.assign(TRANSLATIONS.ar.language, {
  chinese: "الصينية",
  somali: "الصومالية",
});

Object.assign(TRANSLATIONS.ar.language, {
  mandarin: "Mandarin",
});
delete TRANSLATIONS.ar.language.chinese;
delete TRANSLATIONS.ar.language.somali;



Object.assign(TRANSLATIONS.zh.language, {
  mandarin: "Mandarin",
});
delete TRANSLATIONS.zh.language.chinese;
delete TRANSLATIONS.zh.language.somali;

Object.assign(TRANSLATIONS.en, {
  language: {
    label: "Language",
    english: "English",
    arabic: "Arabic",
  },
  nav: {
    ...TRANSLATIONS.en.nav,
    home: "Home",
    shortStay: "Short Stay",
    addProperty: "Add Property",
    privateListings: "Private Listings",
    about: "About Us",
    services: "Services",
    contact: "Contact",
    admin: "Admin",
    logout: "Logout",
    login: "Login",
    loggedOut: "Logged out successfully",
    toggleMenu: "Toggle navigation menu",
    cars: "Cars",
  },
  home: {
    ...TRANSLATIONS.en.home,
    browseByCategory: "Browse by Category",
    availableCategory: "Available {category}",
    categoryProperties: "Properties",
    categoryHouses: "Houses",
    categoryStores: "Warehouses / Stores",
    categoryPlural: "{type}s",
    exploreShortStays: "Explore Short Stays",
    exploreCars: "Explore Cars for Rent or Sale",
    carsEyebrow: "Cars · {count} available",
    carsHeading: "Cars for rent or sale",
    viewAllCars: "View All Cars",
    noCarsAvailable: "No approved cars available yet.",
  },
  propertyTypes: {
    Apartment: "Apartment",
    House: "House",
    Hotel: "Hotel",
    Office: "Office",
    Shop: "Shop",
    Store: "Warehouse / Store",
    Plot: "Plot of Land",
  },
  listingTypes: {
    "For Sale": "For Sale",
    "For Rent": "For Rent",
    "For Lease": "For Lease",
    "Short Stay": "Short Stay",
  },
});

Object.assign(TRANSLATIONS.en.nav, {
  cars: "Cars",
});

Object.assign(TRANSLATIONS.ar.nav, {
  cars: "السيارات",
});

Object.assign(STATIC_TRANSLATIONS.ar, {
  Hotels: "\u0627\u0644\u0641\u0646\u0627\u062f\u0642",
});

Object.assign(STATIC_TRANSLATIONS.ar, {
  "Loading profile...": "جارٍ تحميل الملف الشخصي...",
  "Update your phone number, email, and password.": "حدّث رقم هاتفك وبريدك الإلكتروني وكلمة المرور.",
  "Pending email verification:": "التحقق من البريد الإلكتروني معلّق:",
  "Current password": "كلمة المرور الحالية",
  "Save Profile": "حفظ الملف الشخصي",
  "Change Password": "تغيير كلمة المرور",
  "Confirm new password": "تأكيد كلمة المرور الجديدة",
  "Update Password": "تحديث كلمة المرور",
  "Scroll to bottom": "الانتقال إلى الأسفل",
  "Scroll to top": "الانتقال إلى الأعلى",
  "Search user by name": "ابحث عن مستخدم بالاسم",
  "Pending Approval": "بانتظار الموافقة",
  "No pending users right now.": "لا يوجد مستخدمون بانتظار الموافقة الآن.",
  Created: "تم الإنشاء",
  "Role before review": "الدور قبل المراجعة",
  "Active and Reviewed Users": "المستخدمون النشطون والمراجعون",
  "No reviewed users found.": "لم يتم العثور على مستخدمين تمت مراجعتهم.",
  Role: "الدور",
  "Save Role": "حفظ الدور",
  Reactivate: "إعادة التفعيل",
  Suspend: "تعليق",
  "Your own admin account controls are locked.": "عناصر التحكم في حساب المشرف الخاص بك مقفلة.",
  "Visitor Messages": "رسائل الزوار",
  "No visitor messages have been received yet.": "لم يتم استلام أي رسائل من الزوار بعد.",
  "Recently received": "تم الاستلام مؤخراً",
  "Mark unread": "وضع كغير مقروء",
  "Mark read": "وضع كمقروء",
  "Create New Advert": "إنشاء إعلان جديد",
  "{count} total": "{count} إجمالي",
  "Advert Title": "عنوان الإعلان",
  "Link URL (optional)": "رابط URL اختياري",
  "Description (optional)": "الوصف (اختياري)",
  "media file selected": "ملف وسائط محدد",
  "media files selected": "ملفات وسائط محددة",
  "Upload images or one video": "ارفع صوراً أو فيديو واحداً",
  "Show immediately after upload": "اعرض فوراً بعد الرفع",
  "Upload Advert": "رفع الإعلان",
  "No adverts uploaded yet.": "لم يتم رفع أي إعلانات بعد.",
  "{count} images": "{count} صور",
  "Link URL": "رابط URL",
  "Advert is active": "الإعلان نشط",
  "Checking admin access...": "جارٍ التحقق من صلاحية المشرف...",
  "Owner phone not provided": "لم يتم توفير هاتف المالك",
  Remove: "إزالة",
  "{count} image(s) selected": "تم تحديد {count} صورة",
  "Add property images": "إضافة صور العقار",
  Utilities: "المرافق",
  Agent: "الوكيل",
  Owner: "المالك",
  "Agent not provided": "لم يتم توفير الوكيل",
  "Agent phone not provided": "لم يتم توفير هاتف الوكيل",
  "Phone not provided": "لم يتم توفير الهاتف",
  "Recently added": "أضيف مؤخراً",
  "All Types": "كل الأنواع",
  "{count} properties matched": "{count} عقاراً مطابقاً",
  "Page {current} of {total}": "الصفحة {current} من {total}",
  'Delete "{title}"?': 'حذف "{title}"؟',
  'Delete advert "{title}"?': 'حذف الإعلان "{title}"؟',
  "Delete this visitor message?": "حذف رسالة الزائر هذه؟",
  "Delete this agent account from admin management? This removes the user profile record.":
    "حذف حساب هذا الوكيل من إدارة المشرف؟ سيؤدي ذلك إلى إزالة سجل ملف المستخدم.",
  "Delete this property contact request?": "حذف طلب التواصل الخاص بهذا العقار؟",
  "Delete this hotel service request?": "حذف طلب خدمة الفندق هذا؟",
  "Not provided": "غير متوفر",
  Close: "إغلاق",
  "Select type": "اختر النوع",
  "Select listing type": "اختر نوع الإدراج",
  "Property Type": "نوع العقار",
  "Listing Type": "نوع الإدراج",
  Size: "المساحة",
  "Area in m²": "المساحة بالمتر المربع",
  "Size in m³": "الحجم بالمتر المكعب",
  Toilets: "دورات المياه",
  "Agent Name": "اسم الوكيل",
  "Agent Phone": "هاتف الوكيل",
  "Owner Name": "اسم المالك",
  "Owner Phone": "هاتف المالك",
  "Owner Email": "بريد المالك الإلكتروني",
  Rules: "القواعد",
  "Image URLs": "روابط الصور",
  Subscriptions: "الاشتراكات",
  "Short-stay listings move to about to expire after 25 days, so the subscriber can be contacted by phone.":
    "تنتقل قوائم الإقامة القصيرة إلى حالة قرب الانتهاء بعد 25 يوماً حتى يمكن التواصل مع المشترك هاتفياً.",
  "All notices": "كل الإشعارات",
  "No short-stay subscriptions have been created yet.":
    "لم يتم إنشاء أي اشتراكات إقامة قصيرة بعد.",
  "No subscriptions match this category.": "لا توجد اشتراكات تطابق هذه الفئة.",
  Expires: "ينتهي",
  "not set": "غير محدد",
  "Owner not provided": "لم يتم توفير المالك",
  read: "مقروء",
  unread: "غير مقروء",
});

Object.assign(STATIC_TRANSLATIONS.zh, {
  "Loading profile...": "正在加载个人资料...",
  "Update your phone number, email, and password.": "更新您的电话号码、电子邮箱和密码。",
  "Pending email verification:": "待完成邮箱验证：",
  "Current password": "当前密码",
  "Save Profile": "保存资料",
  "Change Password": "修改密码",
  "Confirm new password": "确认新密码",
  "Update Password": "更新密码",
  "Scroll to bottom": "滚动到底部",
  "Scroll to top": "滚动到顶部",
  "Search user by name": "按姓名搜索用户",
  "Pending Approval": "等待批准",
  "No pending users right now.": "目前没有待批准的用户。",
  Created: "创建时间",
  "Role before review": "审核前角色",
  "Active and Reviewed Users": "已激活和已审核用户",
  "No reviewed users found.": "未找到已审核用户。",
  Role: "角色",
  "Save Role": "保存角色",
  Reactivate: "重新启用",
  Suspend: "暂停",
  "Your own admin account controls are locked.": "您自己的管理员账户控制已锁定。",
  "Visitor Messages": "访客留言",
  "No visitor messages have been received yet.": "尚未收到任何访客留言。",
  "Recently received": "最近收到",
  "Mark unread": "标记为未读",
  "Mark read": "标记为已读",
  "Create New Advert": "创建新广告",
  "{count} total": "共 {count}",
  "Advert Title": "广告标题",
  "Link URL (optional)": "链接 URL（可选）",
  "Description (optional)": "描述（可选）",
  "media file selected": "个媒体文件已选择",
  "media files selected": "个媒体文件已选择",
  "Upload images or one video": "上传图片或一个视频",
  "Show immediately after upload": "上传后立即显示",
  "Upload Advert": "上传广告",
  "No adverts uploaded yet.": "尚未上传任何广告。",
  "{count} images": "{count} 张图片",
  "Link URL": "链接 URL",
  "Advert is active": "广告已启用",
  "Checking admin access...": "正在检查管理员权限...",
  "Owner phone not provided": "未提供业主电话",
  Remove: "移除",
  "{count} image(s) selected": "已选择 {count} 张图片",
  "Add property images": "添加房源图片",
  Utilities: "配套设施",
  Agent: "经纪人",
  Owner: "业主",
  "Agent not provided": "未提供经纪人",
  "Agent phone not provided": "未提供经纪人电话",
  "Phone not provided": "未提供电话",
  "Recently added": "最近添加",
  "All Types": "所有类型",
  "{count} properties matched": "匹配到 {count} 个房源",
  "Page {current} of {total}": "第 {current} 页，共 {total} 页",
  'Delete "{title}"?': '删除“{title}”？',
  'Delete advert "{title}"?': '删除广告“{title}”？',
  "Delete this visitor message?": "删除这条访客留言？",
  "Delete this agent account from admin management? This removes the user profile record.":
    "要从管理员管理中删除此经纪人账户吗？这将移除用户资料记录。",
  "Delete this property contact request?": "删除这条房源联系请求？",
  "Delete this hotel service request?": "删除这条酒店服务请求？",
  "Not provided": "未提供",
  Close: "关闭",
  "Select type": "选择类型",
  "Select listing type": "选择房源类型",
  "Property Type": "房源类型",
  "Listing Type": "发布类型",
  Size: "面积",
  "Area in m²": "面积（平方米）",
  "Size in m³": "体积（立方米）",
  Toilets: "卫生间",
  "Agent Name": "经纪人姓名",
  "Agent Phone": "经纪人电话",
  "Owner Name": "业主姓名",
  "Owner Phone": "业主电话",
  "Owner Email": "业主邮箱",
  Rules: "规则",
  "Image URLs": "图片 URL",
  Subscriptions: "订阅",
  "Short-stay listings move to about to expire after 25 days, so the subscriber can be contacted by phone.":
    "短住房源在 25 天后会进入即将到期状态，以便通过电话联系订阅者。",
  "All notices": "所有通知",
  "No short-stay subscriptions have been created yet.":
    "尚未创建任何短住订阅。",
  "No subscriptions match this category.": "没有符合此分类的订阅。",
  Expires: "到期",
  "not set": "未设置",
  "Owner not provided": "未提供业主",
  read: "已读",
  unread: "未读",
});

Object.assign(STATIC_TRANSLATIONS.so, {
  "Loading profile...": "Akoonka waa la soo gelinayaa...",
  "Update your phone number, email, and password.": "Cusboonaysii lambarkaaga, iimaylkaaga, iyo eraygaaga sirta ah.",
  "Pending email verification:": "Xaqiijinta iimaylka ayaa sugaysa:",
  "Current password": "Erayga sirta ah ee hadda",
  "Save Profile": "Kaydi akoonta",
  "Change Password": "Beddel erayga sirta ah",
  "Confirm new password": "Xaqiiji erayga sirta ah ee cusub",
  "Update Password": "Cusboonaysii erayga sirta ah",
  "Scroll to bottom": "Hoos ugu rog",
  "Scroll to top": "Kor ugu rog",
  "Search user by name": "Isticmaale ku raadi magaca",
  "Pending Approval": "Ansixin sugaya",
  "No pending users right now.": "Hadda ma jiraan isticmaaleyaal sugaya ansixin.",
  Created: "La sameeyay",
  "Role before review": "Doorka ka hor dib-u-eegista",
  "Active and Reviewed Users": "Isticmaaleyaasha firfircoon ee la eegay",
  "No reviewed users found.": "Lama helin isticmaaleyaal la eegay.",
  Role: "Door",
  "Save Role": "Kaydi doorka",
  Reactivate: "Dib u hawlgeli",
  Suspend: "Jooji",
  "Your own admin account controls are locked.": "Xakamaynta akoonkaaga maamulka waa xiran tahay.",
  "Visitor Messages": "Farriimaha booqdayaasha",
  "No visitor messages have been received yet.": "Weli lama helin farriimo booqde.",
  "Recently received": "Dhawaan la helay",
  "Mark unread": "Calaamadee inaan la akhrin",
  "Mark read": "Calaamadee in la akhriyey",
  "Create New Advert": "Samee xayeysiis cusub",
  "{count} total": "{count} wadar",
  "Advert Title": "Cinwaanka xayeysiiska",
  "Link URL (optional)": "URL xiriiriye (ikhtiyaari)",
  "Description (optional)": "Sharaxaad (ikhtiyaari)",
  "media file selected": "fayl warbaahin ah ayaa la doortay",
  "media files selected": "faylal warbaahin ah ayaa la doortay",
  "Upload images or one video": "Soo geli sawirro ama hal fiidiyow",
  "Show immediately after upload": "Muuji isla markiiba marka la soo geliyo",
  "Upload Advert": "Soo geli xayeysiiska",
  "No adverts uploaded yet.": "Weli lama soo gelin wax xayeysiis ah.",
  "{count} images": "{count} sawir",
  "Link URL": "URL xiriiriye",
  "Advert is active": "Xayeysiisku waa firfircoon yahay",
  "Checking admin access...": "Gelitaanka maamulka waa la hubinayaa...",
  "Owner phone not provided": "Telefoonka milkiilaha lama bixin",
  Remove: "Ka saar",
  "{count} image(s) selected": "{count} sawir ayaa la doortay",
  "Add property images": "Ku dar sawirrada hantida",
  Utilities: "Adeegyada",
  Agent: "Wakiil",
  Owner: "Milkiile",
  "Agent not provided": "Wakiilka lama bixin",
  "Agent phone not provided": "Telefoonka wakiilka lama bixin",
  "Phone not provided": "Telefoon lama bixin",
  "Recently added": "Dhawaan lagu daray",
  "All Types": "Dhammaan noocyada",
  "{count} properties matched": "{count} hanti ayaa la helay",
  "Page {current} of {total}": "Bogga {current} ee {total}",
  'Delete "{title}"?': 'Tirtir "{title}"?',
  'Delete advert "{title}"?': 'Tirtir xayeysiiska "{title}"?',
  "Delete this visitor message?": "Ma tirtirtaa farriintan booqdaha?",
  "Delete this agent account from admin management? This removes the user profile record.":
    "Ma ka tirtirtaa akoonkan wakiilka maamulka? Tani waxay saari doontaa diiwaanka astaanta isticmaalaha.",
  "Delete this property contact request?": "Ma tirtirtaa codsigan xiriirka hantida?",
  "Delete this hotel service request?": "Ma tirtirtaa codsigan adeegga hudheelka?",
  "Not provided": "Lama bixin",
  Close: "Xir",
  "Select type": "Dooro nooca",
  "Select listing type": "Dooro nooca liiska",
  "Property Type": "Nooca hantida",
  "Listing Type": "Nooca liiska",
  Size: "Cabbir",
  "Area in m²": "Baaxadda m²",
  "Size in m³": "Cabbirka m³",
  Toilets: "Musqulo",
  "Agent Name": "Magaca wakiilka",
  "Agent Phone": "Telefoonka wakiilka",
  "Owner Name": "Magaca milkiilaha",
  "Owner Phone": "Telefoonka milkiilaha",
  "Owner Email": "Iimaylka milkiilaha",
  Rules: "Xeerar",
  "Image URLs": "URL-yada sawirrada",
  Subscriptions: "Isdiiwaangelinno",
  "Short-stay listings move to about to expire after 25 days, so the subscriber can be contacted by phone.":
    "Liisaska joogitaanka gaaban waxay u gudbaan xaalad ku dhow dhammaad 25 maalmood kadib si qofka isdiiwaangeliyey telefoon loogu la xiriiro.",
  "All notices": "Dhammaan ogeysiisyada",
  "No short-stay subscriptions have been created yet.":
    "Weli lama samayn isdiiwaangelinno joogitaan gaaban ah.",
  "No subscriptions match this category.":
    "Ma jiraan isdiiwaangelinno ku habboon qaybtan.",
  Expires: "Wuu dhacayaa",
  "not set": "Lama dejin",
  "Owner not provided": "Milkiilaha lama bixin",
  read: "la akhriyey",
  unread: "aan la akhrin",
});

Object.assign(STATIC_TRANSLATIONS.so, {
  "Contact admin for price": "La xiriir maamulka qiimaha",
  "Hotel & Corporate Travel Requests": "Codsiyada hudheelka iyo safarka shirkadaha",
  "Coordinate hotels, room blocks, airport pickup, and concierge support for NGOs, embassies, medical teams, and company travelers.":
    "Isku dubbarid hudheello, qolal la xannibay, qaabilaadda garoonka diyaaradaha, iyo taageerada concierge ee NGO-yada, safaaradaha, kooxaha caafimaadka, iyo safarrada shirkadaha.",
  "Send property questions, listing requests, support issues, or partnership inquiries. And our team will review your message.":
    "Soo dir su'aalaha hantida, codsiyada liis-gelinta, arrimaha taageerada, ama weydiimaha iskaashiga, kooxdayaduna way eegi doontaa farriintaada.",
  Administrators: "Maamulayaasha",
  "Reach the Junub Homes administrators directly using the details below.":
    "Si toos ah ula xiriir maamulayaasha Junub Homes adigoo adeegsanaya faahfaahinta hoose.",
  "Loading contact details...": "Faahfaahinta xiriirka waa la soo gelinayaa...",
  "No active administrators are available right now.":
    "Hadda ma jiraan maamulayaal firfircoon oo diyaar ah.",
  "Tap below to view": "Hoos ka taabo si aad u aragto",
  "Opening contact...": "Xiriirka waa la furayaa...",
  "Contact open": "Xiriirka waa furmay",
  "These Terms and Conditions govern how visitors, clients, agents, landlords, sellers, buyers, and other users access and use the Junub Homes website and related services.":
    "Shuruudahan iyo xaaladahani waxay hagayaan sida booqdayaasha, macaamiisha, wakiillada, mulkiilayaasha, iibiyeyaasha, iibsadayaasha, iyo isticmaaleyaasha kale ay u galaan una isticmaalaan website-ka Junub Homes iyo adeegyada la xiriira.",
});

Object.assign(STATIC_TRANSLATIONS.so, {
  "Verified Workflow": "Hab shaqo oo la xaqiijiyey",
  "Listings are reviewed before they appear publicly, helping renters and buyers browse with more confidence.":
    "Liisaska waa la hubiyaa ka hor inta aan si dadweyne loo soo bandhigin, taas oo ka caawisa kiraystayaasha iyo iibsadayaasha inay si kalsooni badan u baadhaan.",
  "Local Listing Focus": "Diiradda liisaska maxalliga ah",
  "Cars in Nairobi": "Baabuurta Nairobi",
  "Clear Connections": "Xiriirro cad",
  "Property seekers can quickly reach the right contact while agents and owners keep their listings organized.":
    "Dadka raadinaya hanti waxay si degdeg ah ula xiriiri karaan qofka saxda ah, halka wakiillada iyo milkiilayaashuna ay liisaskooda si habaysan u maamulaan.",
  "Junub Homes helps people find places to buy, rent, stay, or operate a business, and cars to rent or buy, by bringing approved listings into one clear and searchable experience.":
    "Junub Homes waxay dadka ka caawisaa inay helaan meelo ay iibsadaan, kiraystaan, degaan, ama ganacsi ka wadaan, iyo sidoo kale baabuur ay kiraystaan ama iibsadaan, iyadoo liisaska la ansixiyey ku uruurinaysa hal waayo-aragnimo oo cad oo la baari karo.",
  "Our goal is to reduce the friction between people looking for trusted properties, car rentals, and vehicle sales in Nairobi. The site supports rental homes, sale properties, commercial spaces, stores, short-stay options, and cars, with tools for agents and administrators to keep information current.":
    "Hadafkeenna waa inaan yarayno caqabadaha u dhexeeya dadka raadinaya hanti lagu kalsoonaan karo, kirada baabuurta, iyo iibka gaadiidka ee Nairobi. Boggu wuxuu taageeraa guryaha kirada ah, hantida iibka ah, goobaha ganacsiga, bakhaarada, ikhtiyaarrada joogitaanka gaaban, iyo baabuurta, isaga oo siinaya qalab ay wakiillada iyo maamulayaashu xogta ku cusboonaysiiyaan.",
  "The platform is built around real property and car needs in Nairobi, from homes and apartments to shops, stores, short stays, rentals, and vehicle sales.":
    "Madalku waxa uu ku dhisan yahay baahiyaha dhabta ah ee guryaha iyo baabuurta ee Nairobi, laga bilaabo guryaha iyo dabaqyada ilaa dukaamada, bakhaarada, joogitaanka gaaban, kirada, iyo iibka gaadiidka.",
  "Car rentals and sales sit beside property listings so people can find a home, space, or ride from one trusted place.":
    "Kirada iyo iibka baabuurta waxa lagu daraa liisaska hantida si dadku uga helaan guri, goob, ama gaadiid hal meel oo lagu kalsoon yahay.",
  "We aim to keep listings useful, contacts clear, and the browsing experience fast enough for everyday property and car decisions.":
    "Waxaan hiigsanaynaa in liisasku ahaadaan kuwo waxtar leh, xiriirradu cad yihiin, khibradda baadhistuna ay ku filnaato go'aannada maalinlaha ah ee guryaha iyo baabuurta.",
  "Property & Car Listings": "Liisaska guryaha iyo baabuurta",
  "Publish homes, apartments, commercial spaces, stores, short-stay units, and cars with structured details and images.":
    "Daabac guryo, dabaqyo, goobo ganacsi, bakhaarro, unugyo joogitaan gaaban, iyo baabuur leh faahfaahin iyo sawirro habaysan.",
  "Car Rentals & Sales": "Kirada iyo iibka baabuurta",
  "List cars for rent or sale in Nairobi and help buyers or renters reach Junub Homes quickly.":
    "Ku qor baabuurta kirada ama iibka ah ee Nairobi oo ka caawi iibsadayaasha ama kiraystayaasha inay si degdeg ah u gaaraan Junub Homes.",
  "Property & Car Search": "Raadinta guryaha iyo baabuurta",
  "Browse approved properties and cars by type, price, purpose, and location to narrow decisions faster.":
    "Ku baadh guryaha iyo baabuurta la ansixiyey nooc, qiime, ujeeddo, iyo goob si aad go'aanka ugu soo koobto si degdeg ah.",
  "Listing Review": "Dib-u-eegista liisaska",
  "Admin review tools help keep public listings organized, approved, and easier for visitors to trust.":
    "Qalabka dib-u-eegista maamulka wuxuu ka caawiyaa in liisaska dadweynaha ay ahaadaan kuwo habaysan, la ansixiyey, oo booqdayaashu si fudud ugu kalsoonaadaan.",
  "Listing Marketing": "Suuq-geynta liisaska",
  "Featured advert placements help highlight important offers, agencies, property campaigns, and vehicle deals.":
    "Meelaha xayeysiisyada la xushay waxay iftiimiyaan dalabyada muhiimka ah, wakaaladaha, ololayaasha guryaha, iyo heshiisyada baabuurta.",
  "Start with property rentals, sale properties, short-stay options, or cars and use filters to narrow the list.":
    "Ka bilow guryaha kirada ah, hantida iibka ah, ikhtiyaarrada joogitaanka gaaban, ama baabuurta, kuna isticmaal shaandhooyinka si aad liiska u soo koobto.",
  "Talk to Junub Homes.": "La hadal Junub Homes.",
  "This starter policy explains how Junub Homes may collect, use, and protect information from visitors, property seekers, agents, and property owners.":
    "Siyaasaddan hordhaca ahi waxay sharraxaysaa sida Junub Homes ay u ururin karto, u isticmaali karto, uguna ilaalin karto xogta booqdayaasha, dadka raadinaya hanti, wakiillada, iyo milkiilayaasha.",
  "For privacy questions or requests, contact us at":
    "Su'aalaha ama codsiyada asturnaanta, nagala soo xiriir",
  Introduction: "Hordhac",
  "Acceptance of Terms": "Ogolaanshaha shuruudaha",
  "User Eligibility": "U-qalmitaanka isticmaalaha",
  "Account Registration": "Diiwaangelinta akoonka",
  "Property Listings": "Liisaska hantida",
  "No Legal or Financial Advice": "Ma aha talo sharci ama mid maaliyadeed",
  "Business Affiliates": "La-hawlgalayaasha ganacsiga",
  "Referral Disclosure": "Shaacinta gudbinta",
  "Commissions and Service Fees": "Komishannada iyo khidmadaha adeegga",
  "Third-Party Services": "Adeegyada dhinac saddexaad",
  "User Responsibilities": "Mas'uuliyadaha isticmaalaha",
  "Intellectual Property": "Hantida aqooneed",
  "Privacy and Data Protection": "Asturnaanta iyo ilaalinta xogta",
  "Disclaimer of Warranties": "Diidmada dammaanadaha",
  "Limitation of Liability": "Xaddidaadda mas'uuliyadda",
  Indemnification: "Magdhowga",
  "Force Majeure": "Xaalado ka baxsan awoodda",
  Termination: "Joojinta",
  "Governing Law": "Sharciga xukuma",
  "Dispute Resolution": "Xallinta khilaafaadka",
  Severability: "Kala-goyn sharci",
  "Entire Agreement": "Heshiiska oo dhan",
  "Contact Information": "Macluumaadka xiriirka",
  "Welcome to Junub Homes, a registered real estate brokerage business operating in Nairobi, Republic of Kenya. These Terms govern access to and use of all Junub Homes services, platforms, and website content.":
    "Ku soo dhawoow Junub Homes, oo ah shirkad dillaalnimo hanti maguurto ah oo diiwaangashan kana shaqaysa Nairobi, Jamhuuriyadda Kenya. Shuruudahani waxay xukumayaan gelitaanka iyo isticmaalka dhammaan adeegyada, madallada, iyo waxyaabaha ku jira website-ka Junub Homes.",
  "Junub Homes provides real estate brokerage, property marketing, rentals, leasing, investment advisory, property management referrals, and related consultancy services.":
    "Junub Homes waxay bixisaa adeegyada dillaalnimada hanti maguurto ah, suuq-geynta hantida, kiraynta, liis-gelinta heshiisyada, talo-bixinta maalgashiga, gudbinta maamulka hantida, iyo la-talinta la xiriirta.",
  "By using our Services, you confirm that you have legal capacity to enter binding agreements and agree to comply with all applicable laws.":
    "Markaad isticmaasho Adeegyadeenna, waxaad xaqiijinaysaa inaad sharci ahaan awood u leedahay inaad gasho heshiisyo waajib ah isla markaana aad oggolaatay inaad u hoggaansanto dhammaan sharciyada khuseeya.",
  "Users must be at least 18 years old, not previously suspended from our Services, and legally permitted to use our Services.":
    "Isticmaalayaashu waa inay ugu yaraan jiraan 18 sano, aan hore looga joojin Adeegyadeenna, isla markaana sharci ahaan loo oggol yahay isticmaalkooda.",
  "Users may be required to provide accurate registration information and are responsible for maintaining the confidentiality of account credentials.":
    "Isticmaalayaasha waxaa laga yaabaa in laga rabo inay bixiyaan xog diiwaangelin oo sax ah, waxayna mas'uul ka yihiin ilaalinta sirta aqoonsiga akoonkooda.",
  "Property information may originate from owners, developers, landlords, agents, or third parties. Junub Homes does not guarantee ownership, accuracy, market value, legal status, or suitability of any property.":
    "Macluumaadka hantidu wuxuu ka iman karaa milkiilayaal, horumariyeyaal, mulkiilayaasha kirada, wakiillo, ama dhinacyo saddexaad. Junub Homes ma dammaanad qaaddo lahaanshaha, saxnaanta, qiimaha suuqa, xaaladda sharci, ama ku habboonaanta hanti kasta.",
  "Information provided through the website is for informational purposes only and does not constitute legal, tax, investment, engineering, or financial advice.":
    "Macluumaadka lagu bixiyo website-kan waxa uu ujeedkiisu yahay xog-bixin keliya mana aha talo sharci, canshuureed, maalgashi, injineernimo, ama maaliyadeed.",
  "Junub Homes maintains professional affiliations with Kafuki Jada Blog and N&K Co. Advocates and Legal Consultants.":
    "Junub Homes waxay leedahay xiriirro xirfadeed oo ay la leedahay Kafuki Jada Blog iyo N&K Co. Advocates and Legal Consultants.",
  "Users acknowledge that referrals between affiliated businesses may generate commissions, referral fees, or financial benefits.":
    "Isticmaalayaashu waxay aqoonsan yihiin in gudbinta u dhexeysa ganacsiyada xiriirka leh ay dhalin karto komishanno, khidmadaha gudbinta, ama faa'iidooyin maaliyadeed.",
  "For property rentals, a commission of one month rent for 12 months advanced rent shall be payable for successfully facilitated transactions, and half a month rent for 6 months advanced payment, unless otherwise agreed in writing.":
    "Kirada guryaha, komishan u dhigma hal bil kiradeeda ayaa la bixinayaa marka si guul leh loo fududeeyo heshiis leh 12 bilood oo kirada horay loo bixiyey, iyo nus bil kiradeeda marka 6 bilood horay loo bixiyo, haddii aan si qoraal ah heshiis kale loo gaarin mooyee.",
  "For short stays, including hotels and hotel apartments, and for car rentals, a commission of five percent (5%) of the amount paid shall be charged.":
    "Joogitaanka gaaban, oo ay ku jiraan hudheellada iyo dabaqyada hudheelka, iyo sidoo kale kirada baabuurta, waxaa laga qaadi doonaa komishan dhan shan boqolkiiba (5%) ee lacagta la bixiyey.",
  "For property and car sales, a commission of ten percent (10%) of the total transaction value shall be payable for successfully facilitated transactions unless otherwise agreed in writing.":
    "Iibka guryaha iyo baabuurta, komishan dhan toban boqolkiiba (10%) ee qiimaha guud ee heshiiska ayaa la bixinayaa marka si guul leh loo fududeeyo heshiiska, haddii aan si qoraal ah heshiis kale loo gaarin mooyee.",
  "Links to third-party services are provided for convenience only. Junub Homes does not control or guarantee such services.":
    "Xiriirrada adeegyada dhinac saddexaad waxaa loo bixiyey fududayn keliya. Junub Homes ma maamusho mana dammaanad qaaddo adeegyadaas.",
  "Users shall not provide false information, engage in fraud, misrepresent property ownership, or interfere with website operations.":
    "Isticmaalayaashu ma bixin karaan xog been ah, ma geli karaan khiyaano, si qaldan uma matali karaan lahaanshaha hanti, mana faragelin karaan hawlaha website-ka.",
  "All logos, trademarks, designs, text, graphics, databases, and marketing materials remain the property of Junub Homes or its licensors.":
    "Dhammaan astaamaha, summadaha ganacsiga, naqshadaha, qoraallada, sawirrada, keydadka xogta, iyo agabka suuq-geyntu waxay ahaanayaan hantida Junub Homes ama cidda u siisay rukhsadda.",
  "Users consent to the collection, storage, and processing of personal information necessary for service delivery and legal compliance.":
    "Isticmaalayaashu waxay oggol yihiin ururinta, kaydinta, iyo farsamaynta xogta shakhsiga ah ee lagama maarmaanka u ah bixinta adeegga iyo u hoggaansanaanta sharciga.",
  "Services are provided on an 'AS IS' and 'AS AVAILABLE' basis without warranties of any kind.":
    "Adeegyada waxaa loo bixiyaa sida ay yihiin iyo sida ay u heli karaan, iyada oo aan jirin wax dammaanad ah nooc kasta ha ahaatee.",
  "Junub Homes shall not be liable for direct, indirect, consequential, incidental, or special damages arising from the use of its Services.":
    "Junub Homes mas'uul kama noqon doonto waxyeellooyin toos ah, aan toos ahayn, natiijo ahaan yimaada, kadis ah, ama gaar ah oo ka dhasha isticmaalka Adeegyadeeda.",
  "Users agree to indemnify and hold harmless Junub Homes and its affiliates from claims arising from misuse of Services or breach of these Terms.":
    "Isticmaalayaashu waxay oggol yihiin inay ka magdhabaan kana ilaaliyaan Junub Homes iyo xiriirradeeda sheegasho kasta oo ka dhalata isticmaalka khaldan ee Adeegyada ama jebinta Shuruudahan.",
  "Junub Homes shall not be liable for delays or failures caused by events beyond reasonable control, including natural disasters, conflict, or telecommunications failures.":
    "Junub Homes mas'uul kama noqon doonto dib-u-dhacyo ama fashilaad ka dhasha dhacdooyin ka baxsan xakamaynta macquulka ah, oo ay ku jiraan masiibooyinka dabiiciga ah, colaadaha, ama ciladaha isgaarsiinta.",
  "Junub Homes reserves the right to suspend or terminate access to Services where these Terms are violated.":
    "Junub Homes waxay xaq u leedahay inay joojiso ama xaddido gelitaanka Adeegyada marka Shuruudahani la jebiyo.",
  "These Terms shall be governed by the laws of the Republic of Kenya.":
    "Shuruudahan waxaa xukumi doona sharciyada Jamhuuriyadda Kenya.",
  "Disputes shall first be resolved amicably. Unresolved disputes shall be submitted to competent courts in Kenya.":
    "Khilaafaadka waxaa marka hore lagu xallin doonaa si nabadeed. Haddii aan la xallin, waxaa loo gudbin doonaa maxkamadaha awoodda leh ee Kenya.",
  "If any provision is deemed unenforceable, the remaining provisions shall remain valid and enforceable.":
    "Haddii qodob kasta loo arko mid aan la fulin karin, qodobada kale waxay sii ahaanayaan kuwo sharci ah oo la fulin karo.",
  "These Terms constitute the entire agreement between Junub Homes and users regarding the Services.":
    "Shuruudahani waxay ka kooban yihiin heshiiska oo dhan ee u dhexeeya Junub Homes iyo isticmaalayaasha ee ku saabsan Adeegyada.",
  "Nairobi, Republic of Kenya": "Nairobi, Jamhuuriyadda Kenya",
  "Telephone: 0980430211 / +211929929949": "Telefoon: 0980430211 / +211929929949",
  "Email: virmoduho@gmail.com": "Iimayl: virmoduho@gmail.com",
  "Website: https://junubhomes.com": "Website: https://junubhomes.com",
});

Object.assign(TRANSLATIONS.en.language, {
  somali: "Somali",
});
Object.assign(TRANSLATIONS.ar.language, {
  somali: "الصومالية",
});
Object.assign(TRANSLATIONS.so.language, {
  somali: "Soomaali",
});

Object.assign(TRANSLATIONS.so.nav, {
  home: "Bogga Hore",
  shortStay: "Joogitaan Gaaban",
  addProperty: "Ku Dar Hanti",
  privateListings: "Liisas Gaar Ah",
  about: "Nagu Saabsan",
  services: "Adeegyada",
  contact: "Nala Soo Xiriir",
  admin: "Maamulka",
  logout: "Ka Bax",
  login: "Soo Gal",
  loggedOut: "Si guul leh ayaad uga baxday",
  toggleMenu: "Fur ama xir menu-ga hagaha",
  cars: "Baabuurta",
});

Object.assign(TRANSLATIONS.so, {
  home: {
    ...TRANSLATIONS.so.home,
    heroDefault:
      "Ka hel gurigaaga dhexdiisa gurigaaga xiga ama goobtaada ganacsi.",
    heroListing: "Guryaha {listingType}",
    searchPlaceholder: "Ku raadi cinwaan, goob, ama erey muhiim ah",
    mobileSearchPlaceholder: "Raadi goob ama cinwaan...",
    minPrice: "Qiimaha ugu yar",
    maxPrice: "Qiimaha ugu badan",
    rooms: "Qolalka jiifka",
    propertyType: "Nooca hantida",
    listingType: "Nooca liiska",
    clearFilters: "Nadiifi shaandhooyinka",
    resetFilters: "Dib u deji shaandhooyinka",
    filters: "Shaandhooyin",
    filterProperties: "Shaandhee guryaha",
    browseByCategory: "Ku eeg qayb ahaan",
    availableProperties: "Guryo la heli karo",
    availableCategory: "{category} la heli karo",
    loadingListings: "Liisaska waa la soo gelinayaa...",
    approvedListingsFound: "{count} liis la ansixiyey ayaa la helay",
    loading: "Waa la soo gelinayaa...",
    listingsReady: "{count} liis ayaa diyaar ah",
    allListingTypes: "Dhammaan noocyada liisaska",
    allPropertyTypes: "Dhammaan noocyada hantida",
    loadingApproved: "Guryaha la ansixiyey waa la soo gelinayaa...",
    loadFailed: "Hadda ma soo gelin karno liisaska la ansixiyey.",
    noMatches: "Ma jiraan guryo ku habboon raadinta iyo shaandhooyinka hadda jira.",
    reset: "Dib u celi",
    shortStayCount: "Joogitaan Gaaban · {count} liis{plural}",
    quickStays: "Joogitaanno gaaban oo diyaar ah",
    noShortStays: "Hadda ma jiraan joogitaanno gaaban oo toos ah.",
    previousShortStay: "Muuji joogitaankii gaabnaa ee hore",
    nextShortStay: "Muuji joogitaanka gaaban ee xiga",
    viewAllShortStays: "Eeg dhammaan joogitaanka gaaban",
    exploreShortStays: "Saham joogitaanka gaaban",
    exploreCars: "Saham baabuurta kirada ama iibka ah",
    carsEyebrow: "Baabuurta · {count} ayaa diyaar ah",
    carsHeading: "Baabuurta kirada ama iibka ah",
    viewAllCars: "Eeg dhammaan baabuurta",
    noCarsAvailable: "Weli ma jiraan baabuur la ansixiyey oo la heli karo.",
    contactEyebrow: "La xiriir Junub Homes",
    contactHeading: "Noo sheeg waxaad u baahan tahay",
    name: "Magac",
    phoneNumber: "Lambarka taleefanka",
    subject: "Mowduuc",
    message: "Farriin",
    namePlaceholder: "Magacaaga oo buuxa",
    phonePlaceholder: "Lambarka kuugu habboon ee lagugu helo",
    subjectPlaceholder: "Maxaan kaa caawin karnaa?",
    messagePlaceholder:
      "Noo sheeg nooca guri, miisaaniyadda, goobta, ama su'aal kasta oo aad qabto.",
    validationRequired:
      "Fadlan buuxi magacaaga, lambarka taleefanka, mowduuca, iyo farriinta.",
    validationName: "Magacu waa inuu noqdaa 120 xaraf ama ka yar.",
    validationPhone:
      "Lambarka taleefanku waa inuu noqdaa 7 ilaa 40 xaraf, wuxuuna ka koobnaan karaa tirooyin, meel bannaan, +, -, ama qaws.",
    validationSubject: "Mowduucu waa inuu noqdaa 160 xaraf ama ka yar.",
    validationMessage: "Farriintu waa inay noqotaa 2000 xaraf ama ka yar.",
    messageSent: "Waad ku mahadsan tahay. Farriintaadii waa loo diray kooxda Junub Homes.",
    messageFailed: "Hadda ma diri karno farriintaada. Fadlan mar kale isku day.",
    sending: "Waa la dirayaa...",
    sendMessage: "Dir Farriinta",
    sponsored: "La kafaalo qaaday",
    featuredAdvert: "Xayeysiis la xushay",
    advertisingSpace: "Goob xayeysiis",
    viewAdvert: "Eeg xayeysiiska {number}",
    whyChooseUs: "Maxaad noo dooranaysaa",
    trustedListings: "Liisas la hubiyey",
    clearSearchTools: "Qalab raadis oo cad",
    usefulDetails: "Faahfaahin waxtar leh",
    dismissMessage: "Xir farriinta",
    categoryProperties: "Guryo",
    categoryHouses: "Guryo",
    categoryStores: "Bakhaarro / Dukaamo",
    categoryPlural: "{type}yo",
  },
  details: {
    ...TRANSLATIONS.so.details,
    notFound: "Hantidan lama helin.",
    loadFailed: "Hadda ma soo gelin karno hantidan.",
    loading: "Faahfaahinta hantida waa la soo gelinayaa...",
    unavailable: "Hantidu lama heli karo.",
    locationUnavailable: "Goob lama heli karo",
    listingPrice: "Qiimaha liiska",
    imageView: "{title} sawirka {number}",
    imageThumbnail: "{title} sawir-yare {number}",
    previousImage: "Muuji sawirkii hore ee hantida",
    nextImage: "Muuji sawirka xiga ee hantida",
    bedrooms: "Qolalka jiifka",
    bathrooms: "Musqulaha",
    rooms: "Qolalka jiifka",
    toilets: "Musqulaha",
    propertyType: "Nooca hantida",
    propertyFallback: "Hanti",
    spaceSize: "Baaxadda m²",
    cubicSize: "Cabbirka m³",
    status: "Xaalad",
    statusFallback: "Sugaya",
    description: "Sharaxaad",
    noDescription: "Sharaxaad lama bixin.",
    features: "Astaamaha hantida",
    rules: "Xeerarka guriga",
    shortStayHost: "Faahfaahinta martigeliyaha joogitaanka gaaban",
    name: "Magac:",
    phone: "Taleefan:",
    email: "Iimayl:",
    notProvided: "Lama bixin",
    requestListing: "Codso Liiskan",
    openingContact: "Xiriirka waa la furayaa...",
    contactOpen: "Faahfaahinta xiriirka waa furantahay",
    contactAgent: "La xiriir wakiilka",
    contactSaved:
      "Faahfaahinta xiriirka hadda waa muuqataa. Kooxda maamulka sidoo kale way heshay codsigan.",
    contactSaveFailed:
      "Hadda ma kaydin karno codsigaaga. Weli waxaad la xiriiri kartaa Junub Homes adigoo adeegsanaya faahfaahinta hoose.",
  },
});

Object.assign(STATIC_TRANSLATIONS.so, {
  "Properties and car rentals, sales, and listing support in Nairobi. Contact us to list your property, or explore our website when you are ready to find your next home, space, or ride.":
    "Guryo, kiro iyo iib baabuur, iyo taageerada liis-gelinta ee Nairobi. Nala soo xiriir si aad hantidaada u qorto, ama sahami website-kayaga marka aad diyaar u tahay inaad hesho gurigaaga, goobtaada, ama gaadhigaaga xiga.",
  "Verified Listing": "Liis la xaqiijiyey",
  "Listing Type": "Nooca liiska",
  Rooms: "Qolal",
  Toilets: "Musqulo",
  "Ready to explore": "Diyaar u ah sahaminta",
  "View Details": "Eeg Faahfaahinta",
  "Property title": "Cinwaanka hantida",
  "Select Property Type": "Dooro nooca hantida",
  "Select Listing Type": "Dooro nooca liiska",
  "Number of Rooms": "Tirada qolalka",
  "Number of Toilets": "Tirada musqulaha",
  "Loading car...": "Baabuurka waa la soo gelinayaa...",
  "Car listing not found.": "Lama helin liiska baabuurka.",
  "We could not load this car right now.": "Hadda ma soo gelin karno baabuurkan.",
  "Car unavailable.": "Baabuurka lama heli karo.",
  "Rental Price": "Qiimaha kirada",
  "Car Price": "Qiimaha baabuurka",
  "Previous image": "Sawirkii hore",
  "Next image": "Sawirka xiga",
  Year: "Sannad",
  Body: "Nooca jirka",
  Transmission: "Gear-ka",
  Fuel: "Shidaal",
  Engine: "Matoor",
  Mileage: "Masaafada",
  Drive: "Jiidayn",
  Color: "Midab",
  Negotiable: "La gorgortami karo",
  Yes: "Haa",
  No: "Maya",
  Features: "Astaamo",
  "No features listed.": "Astaamo lama qorin.",
  "Condition Details": "Faahfaahinta xaaladda",
  "Mechanical condition": "Xaaladda mishiinka",
  "Interior condition": "Xaaladda gudaha",
  "Exterior condition": "Xaaladda dibadda",
  "Accident history": "Taariikhda shilalka",
  "Registration history": "Taariikhda diiwaangelinta",
  "Service history": "Taariikhda dayactirka",
  "Contact Junub Homes": "La xiriir Junub Homes",
  "Request car contact": "Codso xiriirka baabuurka",
  Name: "Magac",
  Email: "Iimayl",
  "Not provided": "Lama bixin",
  Hotels: "Hudheellada",
  "Junub Homes Auto": "Junub Homes Auto",
  "Find the right car for work, travel, rent, or ownership.":
    "Hel baabuurka saxda ah ee shaqo, safar, kiro, ama lahaansho.",
  "Search brand, model, location": "Raadi summad, moodal, ama goob",
  "No approved cars match your filters.":
    "Ma jiraan baabuur la ansixiyey oo ku habboon shaandhooyinkaaga.",
  "Why choose us": "Maxaad noo dooranaysaa",
  "Reviewed listings": "Liisaska la hubiyey",
  "Direct contacts": "Xiriir toos ah",
  "Built for teams": "Loogu dhisay kooxo",
  "Admin approval keeps public cars clean and current.":
    "Ansixinta maamulka waxay liisaska baabuurta dadweynaha ka dhigtaa kuwo nadiif ah oo la cusboonaysiiyey.",
  "Call or WhatsApp Junub Homes for car rentals and sales in Nairobi.":
    "Wac ama WhatsApp kula xiriir Junub Homes si aad uga hesho kiro iyo iib baabuur oo Nairobi ah.",
  "Dashboards support ownership, approvals, and listing status.":
    "Dashboard-yadu waxay taageeraan lahaanshaha, ansixinta, iyo xaaladda liisaska.",
  "Hotels, corporate stays, and concierge support for Nairobi.":
    "Hudheello, deganaansho shirkadeed, iyo taageero concierge ah oo Nairobi loogu talagalay.",
  "Junub Homes now gives travelers, NGOs, embassies, and company teams one place to discover hotels, compare trusted options, and send structured accommodation requests.":
    "Junub Homes hadda waxay siisaa safarrayaasha, NGO-yada, safaaradaha, iyo kooxaha shirkadaha hal meel oo ay ka helaan hudheello, ku barbar dhigaan xulashooyin la isku halayn karo, una diraan codsiyo hoy oo nidaamsan.",
  "View Hotels": "Eeg hudheellada",
  "Request Hotel Support": "Codso taageerada hudheelka",
  "approved hotels live": "hudheello la ansixiyey oo toos ah",
  "security-ready hotel options": "ikhtiyaarro hudheel oo amni u diyaarsan",
  "1 request": "1 codsi",
  "for rooms, arrivals, invoices, and add-ons":
    "qolal, imaatin, biillo, iyo adeegyo dheeraad ah",
  "Verified hotel discovery": "Helitaan hudheello la xaqiijiyey",
  "Browse live hotels with pricing, location details, and direct contact routes in one focused Nairobi flow.":
    "Ka dhex eeg hudheello toos ah oo leh qiimeyaal, faahfaahinta goobta, iyo dariiqyo xiriir toos ah oo ku jira khibrad si gaar ah ugu wajahan Nairobi.",
  "Corporate and NGO stays": "Deganaanshaha shirkadaha iyo NGO-yada",
  "Send one structured request for room blocks, long stays, project teams, and conference travel support.":
    "Dir hal codsi oo nidaamsan oo loogu talagalay qolal badan, joogitaan dheer, kooxaha mashruuca, iyo taageerada safarka shirarka.",
  "Arrival coordination": "Isku-dubbaridka imaanshaha",
  "Bundle airport pickup, SIM support, driver requests, translators, and medical travel assistance with accommodation.":
    "Ku dar qaabilaadda garoonka, taageerada SIM-ka, codsiyada darawalka, turnairobiannada, iyo kaalmada safarka caafimaadka si wadajir ah ula socda hoyga.",
  "Invoice-ready booking intake": "Qaadashada dalabyo u diyaarsan qaansheegad",
  "Capture payment preferences, security needs, and budget ranges for teams that book through finance or operations desks.":
    "Qor doorbidyada lacag-bixinta, baahiyaha amniga, iyo xadka miisaaniyadda ee kooxaha ka dalbada xafiisyada maaliyadda ama hawlgalka.",
  "Built for trusted travel": "Loogu talagalay safar lagu kalsoon yahay",
  "Who this hotel service supports": "Cidda adeeggan hudheelku taageero",
  "NGO teams and field staff": "Kooxaha NGO-yada iyo shaqaalaha goobta",
  "Embassy and mission visitors": "Booqdayaasha safaaradaha iyo ergayada",
  "Doctors and medical travel groups": "Dhakhaatiirta iyo kooxaha safarka caafimaadka",
  "Conference and workshop attendees": "Ka-qaybgalayaasha shirarka iyo aqoon-is-weydaarsiyada",
  "Oil, logistics, and construction teams": "Kooxaha shidaalka, saadka, iyo dhismaha",
  "Diaspora guests arranging trusted stays": "Martida qurbajoogta ah ee diyaarinaya joogitaanno la isku halayn karo",
  "View hotels": "Eeg hudheellada",
  "Live hotel listings": "Liisaska hudheellada tooska ah",
  "Use search and budget filters, open hotel details, or start a WhatsApp conversation directly from the listing.":
    "Isticmaal raadinta iyo shaandhooyinka miisaaniyadda, fur faahfaahinta hudheelka, ama si toos ah uga bilow wada hadal WhatsApp liiska dhexdiisa.",
  "Search hotel name or area": "Raadi magaca hudheelka ama aagga",
  "Max nightly budget (USD)": "Miisaaniyadda ugu badan habeenkii (USD)",
  "Loading hotels...": "Hudheellada waa la soo gelinayaa...",
  "No approved hotels match the current filters.":
    "Ma jiraan hudheello la ansixiyey oo ku habboon shaandhooyinka hadda jira.",
  "Request this hotel": "Codso hudheelkan",
  "WhatsApp hotel": "Hudheelka WhatsApp",
  "Direct phone not available yet": "Lambarka tooska ah weli lama helin",
  Security: "Amni",
  "Backup power": "Koronto kayd ah",
  "For hotel partners": "Loogu talagalay la-hawlgalayaasha hudheelada",
  "Visibility, WhatsApp enquiries, and corporate demand in one place":
    "Muuqaal, weydiimaha WhatsApp, iyo baahida shirkadaha oo hal meel ah",
  "Junub Homes is set up to showcase hotel listings professionally, route direct enquiries, and collect higher-value corporate and NGO requests that need rooms, logistics, and invoicing.":
    "Junub Homes waxaa loo diyaariyey inuu si xirfad leh u soo bandhigo liisaska hudheellada, u gudbiyo weydiimaha tooska ah, una ururiyo codsiyada shirkadaha iyo NGO-yada ee qiimaha badan leh kuwaas oo u baahan qolal, saadka, iyo qaansheegad.",
  "Hotel services we offer": "Adeegyada hudheel ee aan bixino",
  "Request accommodation for your team": "Kooxdaada u codso hoy",
  "Share your stay dates, room count, payment setup, and concierge needs so Junub Homes can coordinate the right hotel options.":
    "La wadaag taariikhaha joogitaanka, tirada qolalka, habka lacag-bixinta, iyo baahiyaha concierge si Junub Homes uu kuugu habeeyo xulashooyinka hudheelka ee saxda ah.",
  "Preferred hotel": "Hudheelka la doorbiday",
  "Optional specific hotel": "Hudheel gaar ah oo ikhtiyaari ah",
  "Contact name": "Magaca xiriirka",
  Organization: "Hay'ad / Urur",
  "Company, NGO, mission, or team name": "Magaca shirkadda, NGO-ga, mission-ka, ama kooxda",
  "Organization type": "Nooca ururka",
  Optional: "Ikhtiyaari",
  "Request type": "Nooca codsiga",
  "Stay focus": "Ujeeddada joogitaanka",
  Destination: "Meesha loo socdo",
  "Check-in": "Gelitaanka",
  "Check-out": "Ka bixitaanka",
  "Rooms needed": "Qolalka loo baahan yahay",
  Guests: "Martida",
  "Minimum budget": "Miisaaniyadda ugu yar",
  "Maximum budget": "Miisaaniyadda ugu badan",
  "Payment preference": "Doorbidka lacag-bixinta",
  "Security priority": "Mudnaanta amniga",
  Notes: "Qoraallo",
  "Share preferred areas, invoice needs, hotel standards, or conference details.":
    "La wadaag aagagga aad doorbidayso, baahida biilka, heerarka hudheelka, ama faahfaahinta shirarka.",
  "Sending request...": "Codsiga waa la dirayaa...",
  "Send hotel request": "Dir codsiga hudheelka",
  "Please complete the contact, request type, and destination fields.":
    "Fadlan buuxi xogta xiriirka, nooca codsiga, iyo goobta loo socdo.",
  "Please enter a valid email address.":
    "Fadlan geli cinwaan iimayl sax ah.",
  "Please enter a valid WhatsApp number or leave it blank.":
    "Fadlan geli lambar WhatsApp sax ah ama ka tag bannaan.",
  "Please set the number of rooms needed.":
    "Fadlan caddee tirada qolalka loo baahan yahay.",
  "Please set the expected number of guests.":
    "Fadlan caddee tirada martida la filayo.",
  "Please review the budget range.": "Fadlan dib u eeg xadka miisaaniyadda.",
  "Check-out date must be after check-in date.":
    "Taariikhda bixitaanku waa inay ka dambaysaa taariikhda gelitaanka.",
  "Your hotel service request has been sent to Junub Homes.":
    "Codsigaaga adeegga hudheelka waxaa loo diray Junub Homes.",
  "We could not send your hotel request right now.":
    "Hadda ma diri karno codsigaaga hudheelka.",
  "For property rentals, a commission of ten percent (10%) of the total transaction value shall be payable for successfully facilitated transactions unless otherwise agreed in writing.":
    "Kirada guryaha, komishan dhan toban boqolkiiba (10%) ee qiimaha guud ee macaamilka ayaa la bixinayaa marka si guul leh loo fududeeyo heshiiska, haddii aan si qoraal ah heshiis kale loo gaarin mooyee.",
  USD: "USD",
});

Object.assign(STATIC_TRANSLATIONS.ar, {
  Hotels: "الفنادق",
  "Find the right car for work, travel, rent, or ownership.":
    "اعثر على السيارة المناسبة للعمل أو السفر أو الإيجار أو التملك.",
  "Search brand, model, location": "ابحث بالماركة أو الموديل أو الموقع",
  "No approved cars match your filters.":
    "لا توجد سيارات معتمدة تطابق عوامل التصفية الخاصة بك.",
  "Admin approval keeps public cars clean and current.":
    "تحافظ موافقة الإدارة على تحديث عروض السيارات العامة وتنظيمها.",
  "Call or WhatsApp Junub Homes for car rentals and sales in Nairobi.":
    "اتصل بـ Junub Homes أو راسلنا عبر واتساب لاستئجار السيارات وشرائها في نيروبي.",
  "Dashboards support ownership, approvals, and listing status.":
    "تدعم لوحات التحكم الملكية والموافقات وحالة الإعلانات.",
  "Hotels, corporate stays, and concierge support for Nairobi.":
    "فنادق وإقامات مؤسسية ودعم كونسيرج في نيروبي.",
  "Junub Homes now gives travelers, NGOs, embassies, and company teams one place to discover hotels, compare trusted options, and send structured accommodation requests.":
    "يوفر Junub Homes الآن للمسافرين والمنظمات غير الحكومية والسفارات وفرق الشركات مكانًا واحدًا لاكتشاف الفنادق ومقارنة الخيارات الموثوقة وإرسال طلبات إقامة منظمة.",
  "View Hotels": "عرض الفنادق",
  "Request Hotel Support": "طلب دعم الفنادق",
  "approved hotels live": "فنادق معتمدة متاحة الآن",
  "security-ready hotel options": "خيارات فنادق مجهزة أمنيًا",
  "1 request": "طلب واحد",
  "for rooms, arrivals, invoices, and add-ons":
    "للغرف والوصول والفواتير والخدمات الإضافية",
  "Verified hotel discovery": "اكتشاف فنادق موثوقة",
  "Browse live hotels with pricing, location details, and direct contact routes in one focused Nairobi flow.":
    "تصفح فنادق متاحة مع الأسعار وتفاصيل الموقع وطرق تواصل مباشرة ضمن تجربة مركزة في نيروبي.",
  "Corporate and NGO stays": "إقامات الشركات والمنظمات",
  "Send one structured request for room blocks, long stays, project teams, and conference travel support.":
    "أرسل طلبًا منظمًا واحدًا لحجوزات الغرف والإقامات الطويلة وفرق المشاريع ودعم سفر المؤتمرات.",
  "Arrival coordination": "تنسيق الوصول",
  "Bundle airport pickup, SIM support, driver requests, translators, and medical travel assistance with accommodation.":
    "اجمع الاستقبال من المطار ودعم شرائح الاتصال وطلبات السائقين والمترجمين والمساعدة الطبية للسفر مع الإقامة.",
  "Invoice-ready booking intake": "استقبال حجوزات جاهز للفوترة",
  "Capture payment preferences, security needs, and budget ranges for teams that book through finance or operations desks.":
    "سجّل تفضيلات الدفع واحتياجات الأمان ونطاقات الميزانية للفرق التي تحجز عبر أقسام المالية أو العمليات.",
  "Built for trusted travel": "مصمم لسفر موثوق",
  "Who this hotel service supports": "من يخدمهم هذا القسم الفندقي",
  "NGO teams and field staff": "فرق المنظمات غير الحكومية والموظفون الميدانيون",
  "Embassy and mission visitors": "زوار السفارات والبعثات",
  "Doctors and medical travel groups": "الأطباء ومجموعات السفر العلاجي",
  "Conference and workshop attendees": "المشاركون في المؤتمرات وورش العمل",
  "Oil, logistics, and construction teams": "فرق النفط والخدمات اللوجستية والإنشاءات",
  "Diaspora guests arranging trusted stays": "ضيوف من أبناء الشتات يرتبون إقامات موثوقة",
  "View hotels": "استعراض الفنادق",
  "Live hotel listings": "إعلانات الفنادق المتاحة",
  "Use search and budget filters, open hotel details, or start a WhatsApp conversation directly from the listing.":
    "استخدم البحث وفلاتر الميزانية وافتح تفاصيل الفندق أو ابدأ محادثة واتساب مباشرة من الإعلان.",
  "Search hotel name or area": "ابحث باسم الفندق أو المنطقة",
  "Max nightly budget (USD)": "الحد الأقصى لميزانية الليلة (USD)",
  "Loading hotels...": "جارٍ تحميل الفنادق...",
  "No approved hotels match the current filters.":
    "لا توجد فنادق معتمدة تطابق عوامل التصفية الحالية.",
  "Request this hotel": "اطلب هذا الفندق",
  "WhatsApp hotel": "واتساب الفندق",
  "Direct phone not available yet": "رقم الهاتف المباشر غير متاح بعد",
  "For hotel partners": "لشركاء الفنادق",
  "Visibility, WhatsApp enquiries, and corporate demand in one place":
    "الظهور واستفسارات واتساب والطلب المؤسسي في مكان واحد",
  "Junub Homes is set up to showcase hotel listings professionally, route direct enquiries, and collect higher-value corporate and NGO requests that need rooms, logistics, and invoicing.":
    "تم إعداد Junub Homes لعرض إعلانات الفنادق بشكل احترافي وتوجيه الاستفسارات المباشرة وجمع طلبات الشركات والمنظمات ذات القيمة الأعلى التي تحتاج إلى غرف وخدمات لوجستية وفوترة.",
  "Hotel services we offer": "خدمات الفنادق التي نقدمها",
  "Request accommodation for your team": "اطلب إقامة لفريقك",
  "Share your stay dates, room count, payment setup, and concierge needs so Junub Homes can coordinate the right hotel options.":
    "شارك تواريخ الإقامة وعدد الغرف وترتيبات الدفع واحتياجات الكونسيرج حتى يتمكن Junub Homes من تنسيق خيارات الفندق المناسبة.",
  "Preferred hotel": "الفندق المفضل",
  "Optional specific hotel": "فندق محدد اختياريًا",
  "Contact name": "اسم جهة الاتصال",
  Organization: "الجهة / المؤسسة",
  "Company, NGO, mission, or team name":
    "اسم الشركة أو المنظمة أو البعثة أو الفريق",
  "Organization type": "نوع الجهة",
  Optional: "اختياري",
  "Request type": "نوع الطلب",
  "Stay focus": "طبيعة الإقامة",
  Destination: "الوجهة",
  "Check-in": "تسجيل الدخول",
  "Check-out": "تسجيل المغادرة",
  "Rooms needed": "عدد الغرف المطلوبة",
  Guests: "الضيوف",
  "Minimum budget": "الحد الأدنى للميزانية",
  "Maximum budget": "الحد الأقصى للميزانية",
  "Payment preference": "تفضيل الدفع",
  "Security priority": "أولوية الأمان",
  Notes: "ملاحظات",
  "Share preferred areas, invoice needs, hotel standards, or conference details.":
    "شارك المناطق المفضلة واحتياجات الفواتير ومعايير الفندق أو تفاصيل المؤتمر.",
  "Sending request...": "جارٍ إرسال الطلب...",
  "Send hotel request": "إرسال طلب الفندق",
  "Please complete the contact, request type, and destination fields.":
    "يرجى إكمال بيانات الاتصال ونوع الطلب والوجهة.",
  "Please enter a valid email address.": "يرجى إدخال بريد إلكتروني صحيح.",
  "Please enter a valid WhatsApp number or leave it blank.":
    "يرجى إدخال رقم واتساب صحيح أو اتركه فارغًا.",
  "Please set the number of rooms needed.":
    "يرجى تحديد عدد الغرف المطلوبة.",
  "Please set the expected number of guests.":
    "يرجى تحديد العدد المتوقع للضيوف.",
  "Please review the budget range.": "يرجى مراجعة نطاق الميزانية.",
  "Check-out date must be after check-in date.":
    "يجب أن يكون تاريخ المغادرة بعد تاريخ الوصول.",
  "Your hotel service request has been sent to Junub Homes.":
    "تم إرسال طلب الخدمة الفندقية إلى Junub Homes.",
  "We could not send your hotel request right now.":
    "تعذر إرسال طلب الفندق الآن.",
  "For property rentals, a commission of ten percent (10%) of the total transaction value shall be payable for successfully facilitated transactions unless otherwise agreed in writing.":
    "في إيجارات العقارات، تستحق عمولة بنسبة عشرة بالمئة (10%) من إجمالي قيمة المعاملة للصفقات التي تم تسهيلها بنجاح، ما لم يُتفق على خلاف ذلك كتابةً.",
  "NGO accommodation": "إقامة للمنظمات غير الحكومية",
  "Corporate travel": "سفر الشركات",
  "Conference room block": "حجز غرف للمؤتمر",
  "Long-stay housing": "إقامة طويلة",
  "Secure arrival support": "دعم وصول آمن",
  "Short stay": "إقامة قصيرة",
  "Business hotel": "فندق أعمال",
  "Extended stay": "إقامة ممتدة",
  "Executive stay": "إقامة تنفيذية",
  "Group booking": "حجز جماعي",
  NGO: "منظمة غير حكومية",
  "UN agency": "وكالة أممية",
  Embassy: "سفارة",
  Ministry: "وزارة",
  "Medical organization": "منظمة طبية",
  Business: "شركة",
  "Oil and gas": "النفط والغاز",
  Construction: "الإنشاءات",
  "Invoice and bank transfer": "فاتورة وتحويل بنكي",
  "Card or online payment": "بطاقة أو دفع إلكتروني",
  "Cash on arrival": "الدفع نقدًا عند الوصول",
  "Mixed payment": "دفع مختلط",
  "Standard business stay": "إقامة عمل عادية",
  "Security-conscious location": "موقع عالي الاعتبارات الأمنية",
  "Priority secure access": "أولوية وصول آمن",
  "Airport pickup": "استقبال من المطار",
  "SIM cards": "شرائح اتصال",
  "Driver service": "خدمة سائق",
  "Translator support": "دعم الترجمة",
  "Medical travel support": "دعم السفر العلاجي",
});

function visitTranslationLeaves(dictionary, callback, path = []) {
  if (typeof dictionary === "string") {
    callback(path.join("."), dictionary);
    return;
  }

  if (!dictionary || typeof dictionary !== "object") {
    return;
  }

  Object.entries(dictionary).forEach(([key, value]) => {
    visitTranslationLeaves(value, callback, [...path, key]);
  });
}

function addReverseLookupEntry(map, localizedValue, sourceValue) {
  if (
    typeof localizedValue !== "string" ||
    typeof sourceValue !== "string" ||
    !localizedValue ||
    map.has(localizedValue)
  ) {
    return;
  }

  map.set(localizedValue, sourceValue);
}

const LanguageContext = createContext(null);

function getInitialLanguage() {
  if (typeof window === "undefined") {
    return "en";
  }

  const storedLanguage = window.localStorage.getItem(STORAGE_KEY);

  if (SUPPORTED_LANGUAGES.has(storedLanguage)) {
    return storedLanguage;
  }

  return "en";
}

function interpolate(template, values = {}) {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, String(value)),
    template,
  );
}

function getTranslation(dictionary, path) {
  return path.split(".").reduce((current, segment) => current?.[segment], dictionary);
}

const STATIC_TRANSLATION_REVERSE_LOOKUP = new Map();

Object.values(STATIC_TRANSLATIONS).forEach((dictionary) => {
  Object.entries(dictionary).forEach(([sourceValue, localizedValue]) => {
    addReverseLookupEntry(
      STATIC_TRANSLATION_REVERSE_LOOKUP,
      localizedValue,
      sourceValue,
    );
  });
});

visitTranslationLeaves(TRANSLATIONS.en, (path, englishValue) => {
  Object.values(TRANSLATIONS).forEach((dictionary) => {
    addReverseLookupEntry(
      STATIC_TRANSLATION_REVERSE_LOOKUP,
      getTranslation(dictionary, path),
      englishValue,
    );
  });
});

const PROPERTY_TYPE_REVERSE_LOOKUP = new Map();
const LISTING_TYPE_REVERSE_LOOKUP = new Map();

Object.values(TRANSLATIONS).forEach((dictionary) => {
  Object.entries(dictionary.propertyTypes || {}).forEach(([key, value]) => {
    addReverseLookupEntry(PROPERTY_TYPE_REVERSE_LOOKUP, value, key);
  });

  Object.entries(dictionary.listingTypes || {}).forEach(([key, value]) => {
    addReverseLookupEntry(LISTING_TYPE_REVERSE_LOOKUP, value, key);
  });
});

function canonicalizeStaticText(text) {
  const value = String(text || "");
  return STATIC_TRANSLATION_REVERSE_LOOKUP.get(value) || value;
}

function canonicalizePropertyType(type) {
  const value = String(type || "");
  return PROPERTY_TYPE_REVERSE_LOOKUP.get(value) || value;
}

function canonicalizeListingType(listingType) {
  const value = String(listingType || "");
  return LISTING_TYPE_REVERSE_LOOKUP.get(value) || value;
}

function formatDynamicValue(value, language) {
  const sourceText = String(value || "").trim();

  if (!sourceText) {
    return "";
  }

  if (language === "ar") {
    return transliterateForLanguage(sourceText, language);
  }

  return sourceText;
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    document.documentElement.lang = language;
    document.documentElement.dir = LANGUAGE_DIRECTIONS[language] || "ltr";
  }, [language]);

  const value = useMemo(() => {
    function setLanguage(nextLanguage) {
      if (!SUPPORTED_LANGUAGES.has(nextLanguage)) {
        return;
      }

      setLanguageState(nextLanguage);
      window.localStorage.setItem(STORAGE_KEY, nextLanguage);
    }

    function t(path, values) {
      const localizedTemplate = getTranslation(TRANSLATIONS[language], path);
      const englishTemplate = getTranslation(TRANSLATIONS.en, path);
      const template =
        localizedTemplate ||
        (language !== "en" && typeof englishTemplate === "string"
          ? STATIC_TRANSLATIONS[language]?.[englishTemplate] || englishTemplate
          : englishTemplate) ||
        path;

      return replaceBrandName(interpolate(template, values));
    }

    function formatPropertyType(type) {
      const canonicalType = canonicalizePropertyType(type);
      return (
        TRANSLATIONS[language].propertyTypes[canonicalType] ||
        TRANSLATIONS.en.propertyTypes[canonicalType] ||
        canonicalType ||
        ""
      );
    }

    function formatListingType(listingType) {
      const canonicalListingType = canonicalizeListingType(listingType);
      return (
        TRANSLATIONS[language].listingTypes[canonicalListingType] ||
        TRANSLATIONS.en.listingTypes[canonicalListingType] ||
        canonicalListingType ||
        ""
      );
    }

    function translateStatic(text, values) {
      const canonicalText = canonicalizeStaticText(text);
      const template =
        language === "en"
          ? canonicalText
          : STATIC_TRANSLATIONS[language]?.[canonicalText] || canonicalText;

      return replaceBrandName(interpolate(template, values));
    }

    function formatCurrencyLabel(currency) {
      return translateStatic(currency);
    }

    function formatPlainText(text) {
      return String(text || "").trim();
    }

    function formatDynamicText(text) {
      const canonicalText = canonicalizeStaticText(text);
      const staticTranslation =
        language === "en"
          ? canonicalText
          : STATIC_TRANSLATIONS[language]?.[canonicalText];

      if (staticTranslation) {
        return staticTranslation;
      }

      return formatDynamicValue(canonicalText, language);
    }

    function formatLocationText(text) {
      const canonicalText = canonicalizeStaticText(text);
      const staticTranslation =
        language === "en"
          ? canonicalText
          : STATIC_TRANSLATIONS[language]?.[canonicalText];

      if (staticTranslation) {
        return staticTranslation;
      }

      return translateLocationTextForLanguage(canonicalText, language);
    }

    return {
      language,
      locale: LANGUAGE_LOCALES[language] || "en-US",
      direction: LANGUAGE_DIRECTIONS[language] || "ltr",
      setLanguage,
      t,
      translateStatic,
      canonicalizeStaticText,
      canonicalizePropertyType,
      canonicalizeListingType,
      formatCurrencyLabel,
      formatPlainText,
      formatDynamicText,
      formatLocationText,
      formatPropertyType,
      formatListingType,
    };
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used inside LanguageProvider.");
  }

  return context;
}
